import React, { useEffect, useRef, useState } from 'react';

// Waveform envelope resolution: RMS peaks per second stored for the overview
// waveform. Decoded audio is downsampled to this rate so the stored array stays
// tiny (a 2-hour recording is only ~720k floats) instead of holding tens of
// millions of raw PCM samples. Every "buffer length -> seconds" conversion below
// divides by this rate.
const WAVEFORM_ENVELOPE_RATE = 100;

// Vertical (amplitude) zoom limits. The waveform is normalised to the loudest
// point in view, so a single loud spike can make the rest of the signal tiny —
// a high ceiling lets that quiet detail be magnified enough to inspect.
const AMP_MIN = 0.1;
const AMP_MAX = 2000;
// Below this view span (seconds) the RMS envelope is too coarse, so we decode and
// overlay the real per-sample waveform for the visible range.
const SAMPLE_VIEW_MAX_SEC = 0.75;
// WaveformViewer component that supports real-time updates during streaming
const WaveformViewer = ({ 
  isStreaming, // boolean indicating if we're currently streaming
  audioUrl, // MediaSource URL to connect to for real audio analysis
  onReady, // callback when waveform is ready
  registerRawAudioSink, // register a lossless sink that receives raw fMP4 chunks (and reset signals)
  onTimeSelected, // callback when user clicks on waveform to select time
  comments, // array of comments to display as markers on waveform
  onCommentClick, // callback when user clicks on a comment marker
  highlightedCommentUuid, // ID of comment to highlight
  onCurrentTimeChange // callback to expose current time and seeking capabilities
}) => {
  const waveformRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const audioContextRef = useRef(null);
  const [audioElement, setAudioElement] = useState(null);
  const audioElementRef = useRef(null); // Add persistent ref for audio element
  // Tracks the most recent HTMLMediaElement.play() promise. play() resolves
  // asynchronously; pausing/seeking before it settles rejects it with an
  // AbortError ("The play() request was interrupted by a call to pause()").
  // We keep the promise so pause can wait for it, and always swallow the reject.
  const playPromiseRef = useRef(null);

  // Store accumulated audio samples for complete waveform
  const accumulatedSamplesRef = useRef([]);
  const rawDataBufferRef = useRef(new Uint8Array(0)); // Buffer for accumulating raw M4A data
  const hasDecodedAnyDataRef = useRef(false);
  // Incremental fMP4 waveform decoding state. Instead of re-decoding the WHOLE
  // growing file on every pass (which is O(n^2) and, past ~100 min at 48 kHz,
  // exceeds the browser's decodeAudioData memory ceiling so every later decode
  // throws and the waveform silently freezes), we decode only the NEW fMP4
  // fragments since envDecodeCursorRef and append their RMS envelope.
  const envInitSegmentRef = useRef(null);   // captured ftyp+moov init segment (Uint8Array)
  const envDecodeCursorRef = useRef(0);      // byte offset in rawDataBufferRef enveloped so far
  const envBucketSizeRef = useRef(0);        // decoded samples per envelope bucket (set on first decode)
  const envCarrySumSqRef = useRef(0);        // (legacy) carried sum-of-squares - unused since buckets are time-anchored
  const envCarryCountRef = useRef(0);        // (legacy) carried sample count - unused since buckets are time-anchored
  const envTimescaleRef = useRef(0);         // media timescale (units/sec) parsed from the init segment's mdhd
  // Waveform decode throttling: decodeAudioData needs the WHOLE fMP4 file and is
  // O(file), so decoding on every incoming chunk is O(n^2) and starves the CPU
  // (breaking playback), while racing async decodes make the waveform flicker.
  const waveformDecodingRef = useRef(false);      // single-flight guard
  const waveformDecodePendingRef = useRef(false); // more data arrived mid-decode
  const waveformDecodeTimerRef = useRef(null);    // debounce timer id
  const lastWaveformDecodeRef = useRef(0);        // timestamp of last decode start
  const commentsRef = useRef([]); // Store comments in ref for immediate access
  const lastDrawTimeRef = useRef(0); // Debouncing for marker redraws
  const resizeTimerRef = useRef(null); // For resize debounce
  
  // NEW: Playback cursor tracking
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const cursorAnimationRef = useRef(null);
  const lastCanvasRef = useRef(null);
  // Offscreen bitmap cache of the (expensive) waveform background. The playback cursor
  // RAF loop blits this instead of re-scanning all samples every frame.
  const waveformCacheRef = useRef(null);
  const waveformCacheKeyRef = useRef(null);
  
  // NEW: Audio gain control
  const [gain, setGain] = useState(1.0); // Default gain = 1.0 (0dB)
  const gainNodeRef = useRef(null);
  const mediaElementSourceRef = useRef(null);
  const connectedAudioElementRef = useRef(null); // Track which audio element is connected to Web Audio API

  // Audacity-style browsing over the streamed envelope. The waveform we draw is the
  // incrementally-decoded RMS envelope (accumulatedSamplesRef) and playback runs
  // through the SAME <audio>/MediaSource element, so zoom / selection / loop all
  // operate live on the streamed FLAC (no full-file decode needed).
  // View window is in seconds; viewEnd === 0 means "auto: follow the full (growing) length".
  const [viewStart, setViewStart] = useState(0);
  const [viewEnd, setViewEnd] = useState(0);
  const [ampScale, setAmpScale] = useState(1);
  const [ampOffset, setAmpOffset] = useState(0); // vertical pan 0..1 when amplitude-zoomed (0 = baseline, 1 = peaks)
  const [selection, setSelection] = useState(null); // { start, end } in seconds, or null
  const [loopEnabled, setLoopEnabled] = useState(false);
  // Right-click menu over the waveform, and the tabbed analysis modal it opens.
  const [contextMenu, setContextMenu] = useState(null);   // { x, y } screen coords, or null
  const [analysis, setAnalysis] = useState(null);          // { stats, spec, spectrogram, note, sampleRate, startSec, endSec } | { error }
  const [analysisTab, setAnalysisTab] = useState('spectrum'); // 'spectrum' | 'stats' | 'spectrogram'
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const [specScale, setSpecScale] = useState('log');       // 'log' | 'linear'
  const [specMode, setSpecMode] = useState('avg');         // 'avg' | 'peak'
  const [specHarmonics, setSpecHarmonics] = useState(true);
  const [specCursor, setSpecCursor] = useState(null);      // { f, db } under the pointer
  const spectrumCanvasRef = useRef(null);
  const spectrogramCanvasRef = useRef(null);
  // Transport / listening aids.
  const [playbackRate, setPlaybackRate] = useState(1);
  const [filterType, setFilterType] = useState('off');     // 'off' | 'highpass' | 'lowpass' | 'bandpass'
  const [filterFreq, setFilterFreq] = useState(1000);
  const filterNodeRef = useRef(null);
  // Delta-time measure tool and auto-detected transients (local overlays).
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);  // up to 2 times (seconds)
  const measureModeRef = useRef(false);
  const measurePointsRef = useRef([]);
  const [transients, setTransients] = useState([]);        // times (seconds) of detected onsets
  const transientsRef = useRef([]);
  // Cached sample-level PCM overlay for the current zoomed-in view.
  const sampleOverlayRef = useRef(null);                   // { start, end, samples, sampleRate } | null
  const sampleOverlayTimerRef = useRef(null);
  const viewRef = useRef({ start: 0, end: 0 });
  const ampRef = useRef(1);
  const ampOffsetRef = useRef(0);
  const selectionRef = useRef(null);
  const loopRef = useRef(false);
  const playSelectionRef = useRef(false); // playback currently bounded to the selection region
  const dragRef = useRef(null); // { downX, downTime, moved } while dragging a selection

  // Keep commentsRef in sync with comments prop
  useEffect(() => {
    const newComments = comments || [];
    // Only update if comments actually changed
    if (JSON.stringify(commentsRef.current) !== JSON.stringify(newComments)) {
      commentsRef.current = newComments;
      //console.log('WaveForm: Comments ref updated', commentsRef.current.length);
    }
  }, [comments]); // Cache canvas for cursor updates

  // Mirror zoom/selection/loop state into refs so the imperative draw and the audio
  // element's timeupdate handler always read the latest values (never stale closures).
  useEffect(() => { viewRef.current = { start: viewStart, end: viewEnd }; }, [viewStart, viewEnd]);
  useEffect(() => { ampRef.current = ampScale; }, [ampScale]);
  useEffect(() => { ampOffsetRef.current = ampOffset; }, [ampOffset]);
  useEffect(() => { selectionRef.current = selection; }, [selection]);
  useEffect(() => { loopRef.current = loopEnabled; }, [loopEnabled]);
  useEffect(() => { measureModeRef.current = measureMode; }, [measureMode]);
  useEffect(() => { transientsRef.current = transients; }, [transients]);
  useEffect(() => { measurePointsRef.current = measurePoints; }, [measurePoints]);

  const cursorTrackingCleanupRef = useRef(null); // Store cleanup function for cursor tracking
  
  // Helper function to convert hh:mm:ss.ms to seconds
  const timeStringToSeconds = (timeString) => {
    if (!timeString || timeString === '') return 0;
    
    const parts = timeString.split(':');
    if (parts.length !== 3) return 0;
    
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const secondsAndMs = parseFloat(parts[2]) || 0;
    
    return hours * 3600 + minutes * 60 + secondsAndMs;
  };

  // Total length of the recording in seconds, measured on the ENVELOPE's own
  // timeline (one bucket = 1/WAVEFORM_ENVELOPE_RATE s of decoded audio). The whole
  // seconds-domain of this component -- the visible view window, the playback
  // cursor, comment markers, zoom and selection -- is anchored to this single
  // timeline so they always stay mutually consistent. We deliberately do NOT mix in
  // the <audio> element's duration here: while streaming (MSE) that duration is
  // Infinity/estimated, and blending it with the envelope timeline makes the drawn
  // waveform and the cursor drift apart. The envelope is exactly what is drawn, and
  // it is decoded from the same audio that plays, so mapping the real playback time
  // onto this timeline keeps the cursor aligned with what is heard.
  const getTotalDuration = () => {
    return accumulatedSamplesRef.current.length / WAVEFORM_ENVELOPE_RATE;
  };

  // Resolve the current visible window in seconds. viewEnd === 0 (or <= start) means
  // "auto": follow the whole growing recording.
  const getView = () => {
    const total = getTotalDuration();
    const vs = Math.max(0, viewRef.current.start);
    const ve = viewRef.current.end > viewRef.current.start ? viewRef.current.end : total;
    return { start: vs, end: Math.max(ve, vs + 0.001), total };
  };

  // Map a client X (mouse) coordinate to a time (seconds) within the current view.
  const xToTime = (clientX) => {
    const canvas = lastCanvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    const view = getView();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    return view.start + Math.max(0, Math.min(1, ratio)) * (view.end - view.start);
  };

  // Unified drawing function to eliminate race conditions
  const drawCompleteWaveform = (options = {}) => {
    const { 
      showCursor = false, 
      cursorTime = 0,
      forceRedraw = false 
    } = options;
    
    const canvas = lastCanvasRef.current;
    if (!canvas)
    {
      return;
    }
    
    const audioData = accumulatedSamplesRef.current;
    if (audioData.length === 0)
    {
      return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Current visible window (seconds) + amplitude zoom. Everything below maps the
    // envelope indices for [view.start, view.end] across the canvas width, so zooming
    // in time works without touching the stored data or the streamed playback.
    const view = getView();
    const viewSpan = Math.max(0.001, view.end - view.start);
    const amp = ampRef.current;
    const durationSeconds = view.total;

    const startIdx = Math.max(0, Math.floor(view.start * WAVEFORM_ENVELOPE_RATE));
    const endIdx = Math.min(audioData.length, Math.ceil(view.end * WAVEFORM_ENVELOPE_RATE));
    const viewCount = Math.max(1, endIdx - startIdx);

    // Vertical pan: when amplitude-zoomed the loudest peak (0.8 * amp of the height)
    // can exceed the viewport, so ampOffset (0 = baseline at bottom, 1 = peaks at top)
    // shifts the drawing to scroll the clipped-off part into view.
    const vOverflow = Math.max(0, 0.8 * amp - 1);
    const vOffsetPixels = ampOffsetRef.current * vOverflow * canvas.height;

    // Rebuild the cached waveform background only when the data/size/view/amp changes.
    // This scan is O(samples); doing it per cursor frame is what froze playback.
    const cacheKey = `${audioData.length}|${canvas.width}|${canvas.height}|${isStreaming ? 1 : 0}|${view.start.toFixed(3)}|${view.end.toFixed(3)}|${amp.toFixed(2)}|${ampOffsetRef.current.toFixed(3)}`;
    if (forceRedraw || !waveformCacheRef.current || waveformCacheKeyRef.current !== cacheKey)
    {
      let cache = waveformCacheRef.current;
      if (!cache)
      {
        cache = document.createElement('canvas');
        waveformCacheRef.current = cache;
      }
      cache.width = canvas.width;
      cache.height = canvas.height;
      const cctx = cache.getContext('2d');
      cctx.clearRect(0, 0, cache.width, cache.height);

      // Calculate waveform parameters over the visible index window only.
      const centerY = cache.height;

      // Map each pixel column to a fractional slice of the visible sample window so
      // the waveform always fills the full canvas width in proportion to the view.
      // This keeps the drawn waveform aligned with the selection overlay, the
      // playback cursor and xToTime() (all of which map linearly across the width).
      const sampleAtPixel = (px) => startIdx + Math.floor((px / cache.width) * viewCount);

      let maxAmplitude = 0;
      for (let pixelX = 0; pixelX < cache.width; pixelX++)
      {
        const startSample = sampleAtPixel(pixelX);
        const endSample = Math.min(endIdx, Math.max(startSample + 1, sampleAtPixel(pixelX + 1)));

        if (startSample >= endIdx) break;

        let sum = 0;
        for (let i = startSample; i < endSample; i++)
        {
          sum += audioData[i] * audioData[i];
        }
        const rms = Math.sqrt(sum / Math.max(1, endSample - startSample));
        maxAmplitude = Math.max(maxAmplitude, Math.abs(rms));
      }
      if (maxAmplitude <= 0) maxAmplitude = 1;

      // Draw dB scale on y-axis
      // Reference: 0 dB = maxAmplitude (normalized to 1.0)
      const maxAmplitudeDb = maxAmplitude > 0 ? 20 * Math.log10(maxAmplitude) : -Infinity;

      cctx.fillStyle = '#888';
      cctx.font = '10px monospace';
      cctx.textAlign = 'left';
      cctx.textBaseline = 'middle';

      const dbStepCandidates = [1, 2, 3, 6, 12, 24, 48];
      const targetDbGapPx = 42;
      let dbStep = dbStepCandidates[dbStepCandidates.length - 1];
      for (const s of dbStepCandidates) {
        const gapPx = centerY * 0.8 * amp * (1 - Math.pow(10, -s / 20));
        if (gapPx >= targetDbGapPx) { dbStep = s; break; }
      }

      for (let k = 0; k < 80; k++) {
        const dbOffset = -dbStep * k;
        const amplitudeRatio = Math.pow(10, dbOffset / 20); // dB to linear
        const yOffset = centerY * 0.8 * amplitudeRatio * amp;
        const yTop = centerY - yOffset + vOffsetPixels;
        if (yTop < 8) continue;        // still above the visible top -> try a lower line
        if (yTop > centerY) break;     // past the baseline -> nothing lower is visible
        const absoluteDb = maxAmplitudeDb + dbOffset;
        cctx.fillStyle = '#888';
        cctx.fillText(`${absoluteDb.toFixed(1)}dB`, 5, yTop);
        cctx.strokeStyle = '#e0e0e0';
        cctx.lineWidth = 0.5;
        cctx.setLineDash([2, 2]);
        cctx.beginPath();
        cctx.moveTo(60, yTop);
        cctx.lineTo(cache.width, yTop);
        cctx.stroke();
        cctx.setLineDash([]);
      }

      let y_values = new Array(cache.width).fill(0);
      for (let pixelX = 0; pixelX < cache.width; pixelX++)
      {
        const startSample = sampleAtPixel(pixelX);
        const endSample = Math.min(endIdx, Math.max(startSample + 1, sampleAtPixel(pixelX + 1)));

        if (startSample >= endIdx) break;

        let sum = 0;
        for (let i = startSample; i < endSample; i++)
        {
          sum += audioData[i] * audioData[i];
        }
        const rms = Math.sqrt(sum / Math.max(1, endSample - startSample));
        y_values[pixelX] = 0.8 * (rms / maxAmplitude) * amp;
      }

      // Draw waveform
      cctx.strokeStyle = isStreaming ? '#4f8edb' : '#2196f3';
      cctx.lineWidth = 1.5;
      cctx.beginPath();

      for (let pixelX = 0; pixelX < cache.width; pixelX++)
      {
        const y = centerY - centerY * y_values[pixelX] + vOffsetPixels;
        cctx.lineTo(pixelX, y);
        if (pixelX === 0)
        {
          cctx.moveTo(pixelX, y);
        }
        else
        {
          cctx.lineTo(pixelX, y);
        }
      }
      cctx.strokeStyle = isStreaming ? '#4f8edb' : '#2196f3';
      cctx.lineWidth = 1;
      cctx.stroke();

      // Adaptive horizontal (time) scale: choose a "nice" step so tick labels stay
      // ~evenly spaced across whatever time window is currently zoomed to.
      const timeStepCandidates = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];
      const targetTimeTicks = Math.max(2, Math.floor(cache.width / 90));
      let timeStep = timeStepCandidates[timeStepCandidates.length - 1];
      for (const s of timeStepCandidates) {
        if (viewSpan / s <= targetTimeTicks) { timeStep = s; break; }
      }
      const fmtAxisTime = (t) => {
        const m = Math.floor(t / 60);
        const sec = t - m * 60;
        if (timeStep < 1) return `${m}:${sec.toFixed(2).padStart(5, '0')}`;
        return `${m}:${Math.floor(sec).toString().padStart(2, '0')}`;
      };
      cctx.font = '10px monospace';
      cctx.textBaseline = 'top';
      const firstTick = Math.ceil(view.start / timeStep - 1e-6) * timeStep;
      const tickCount = Math.floor((view.end - firstTick) / timeStep + 1e-6) + 1;
      for (let i = 0; i < tickCount && i < 500; i++) {
        const t = firstTick + i * timeStep;
        const x = ((t - view.start) / viewSpan) * cache.width;
        if (x < 62 || x > cache.width - 42) continue;
        cctx.strokeStyle = '#ececec';
        cctx.lineWidth = 0.5;
        cctx.beginPath();
        cctx.moveTo(x, 0);
        cctx.lineTo(x, cache.height);
        cctx.stroke();
        cctx.fillStyle = '#999';
        cctx.textAlign = 'center';
        cctx.fillText(fmtAxisTime(t), x, 2);
      }

      // Draw duration info
      const minutes = Math.floor(durationSeconds / 60);
      const seconds = Math.floor(durationSeconds % 60);

      cctx.fillStyle = '#666';
      cctx.font = '12px Arial';
      cctx.textAlign = 'right';
      cctx.textBaseline = 'top';
      cctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, cache.width - 10, 4);

      waveformCacheKeyRef.current = cacheKey;
    }

    // Blit the cached background, then draw the live cursor overlay (cheap per-frame path).
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(waveformCacheRef.current, 0, 0);

    // Comment markers are now drawn as HTML elements positioned over the canvas
    // (see the CommentMarkers component in the return statement)

    // Selection overlay (mapped into the current view window).
    const sel = selectionRef.current;
    if (sel && sel.end > sel.start) {
      const xs = ((sel.start - view.start) / viewSpan) * canvas.width;
      const xe = ((sel.end - view.start) / viewSpan) * canvas.width;
      const cxs = Math.max(0, Math.min(canvas.width, xs));
      const cxe = Math.max(0, Math.min(canvas.width, xe));
      if (cxe > cxs) {
        ctx.fillStyle = 'rgba(212,175,55,0.18)';
        ctx.fillRect(cxs, 0, cxe - cxs, canvas.height);
        ctx.strokeStyle = 'rgba(212,175,55,0.9)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(cxs, 0); ctx.lineTo(cxs, canvas.height); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cxe, 0); ctx.lineTo(cxe, canvas.height); ctx.stroke();
      }
    }

    // Transient markers (auto-detected onsets) in the current view window.
    const trs = transientsRef.current;
    if (trs && trs.length) {
      ctx.strokeStyle = 'rgba(59,130,246,0.85)';
      ctx.lineWidth = 1;
      for (const t of trs) {
        if (t < view.start || t > view.end) continue;
        const x = ((t - view.start) / viewSpan) * canvas.width;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
    }

    // Measure-tool click points + connecting span.
    const mps = measurePointsRef.current;
    if (mps && mps.length) {
      ctx.strokeStyle = 'rgba(138,109,31,0.95)';
      ctx.fillStyle = 'rgba(138,109,31,0.95)';
      ctx.lineWidth = 1;
      const xs2 = mps.map((t) => ((t - view.start) / viewSpan) * canvas.width);
      xs2.forEach((x) => { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke(); });
      if (xs2.length === 2) {
        ctx.beginPath(); ctx.moveTo(xs2[0], 12); ctx.lineTo(xs2[1], 12); ctx.stroke();
      }
    }

    // Sample-level PCM overlay (real waveform shape) when zoomed in and available.
    const so = sampleOverlayRef.current;
    if (so && so.samples && view.end - view.start <= SAMPLE_VIEW_MAX_SEC
        && view.start >= so.start - 0.01 && view.end <= so.end + 0.01) {
      const sr = so.sampleRate;
      const midY = canvas.height / 2;
      ctx.strokeStyle = 'rgba(34,211,238,0.9)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      let started = false;
      for (let px = 0; px < canvas.width; px++) {
        const t = view.start + (px / canvas.width) * viewSpan;
        const idx = Math.round((t - so.start) * sr);
        if (idx < 0 || idx >= so.samples.length) continue;
        const y = midY - so.samples[idx] * amp * midY * 0.9;
        if (!started) { ctx.moveTo(px, y); started = true; } else { ctx.lineTo(px, y); }
      }
      ctx.stroke();
    }

    // Draw cursor if requested (mapped into the current view window).
    if (showCursor && cursorTime >= view.start && cursorTime <= view.end) {
      const cursorX = ((cursorTime - view.start) / viewSpan) * canvas.width;
      if (cursorX >= 0 && cursorX <= canvas.width) {
        // Draw cursor line
        ctx.strokeStyle = isPlaying ? '#ff4444' : '#ff8888';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        
        ctx.beginPath();
        ctx.moveTo(cursorX, 0);
        ctx.lineTo(cursorX, canvas.height);
        ctx.stroke();

        // Draw time tooltip
        const currentMinutes = Math.floor(cursorTime / 60);
        const currentSeconds = Math.floor(cursorTime % 60);
        const timeText = `${currentMinutes}:${currentSeconds.toString().padStart(2, '0')}`;
        
        ctx.fillStyle = '#333';
        ctx.font = '11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(timeText, cursorX, canvas.height - 15);
      }
    }
  };
  
  // Process raw audio data chunks for direct waveform analysis.
  // These arrive as a lossless byte STREAM via a registered sink (NOT React state),
  // so no chunk is ever dropped and the app is not re-rendered per chunk.
  const handleIncomingRawAudio = (rawAudioData) => {
    if (rawAudioData === null || (rawAudioData && rawAudioData.reset)) {
      // Reset case: clear all accumulated audio data
      resetWaveformEnvelope();
      rawDataBufferRef.current = new Uint8Array(0);
      hasDecodedAnyDataRef.current = false;
      // Reset waveform-decode throttling state
      waveformDecodingRef.current = false;
      waveformDecodePendingRef.current = false;
      if (waveformDecodeTimerRef.current !== null)
      {
        clearTimeout(waveformDecodeTimerRef.current);
        waveformDecodeTimerRef.current = null;
      }
      lastWaveformDecodeRef.current = 0;
      // Invalidate the cached waveform bitmap so the next file rebuilds it
      waveformCacheKeyRef.current = null;
      // Don't clear lastCanvasRef to preserve click handlers
      
      // Clear the canvas content but preserve the canvas element and its handlers
      const canvas = waveformRef.current;
      if (canvas && typeof canvas.getContext === 'function')
      {
        const ctx = canvas.getContext('2d');
        if (ctx)
        {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
      
      // Reset playback state
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);

      // Reset the view (time/zoom), amplitude and selection so the new file starts fresh.
      setViewStart(0);
      setViewEnd(0);
      setAmpScale(1);
      setAmpOffset(0);
      setSelection(null);
      setMeasurePoints([]);
      setTransients([]);
      viewRef.current = { start: 0, end: 0 };
      ampRef.current = 1;
      ampOffsetRef.current = 0;
      selectionRef.current = null;
      playSelectionRef.current = false;
      measurePointsRef.current = [];
      transientsRef.current = [];
      
      // Immediately clear audio element to prevent cursor conflicts
      setAudioElement(null);
      audioElementRef.current = null;
      
      // Stop any ongoing cursor animation and cleanup cursor tracking
      if (cursorAnimationRef.current)
      {
        cancelAnimationFrame(cursorAnimationRef.current);
        cursorAnimationRef.current = null;
      }
      
      // Cleanup previous cursor tracking to avoid conflicts
      if (cursorTrackingCleanupRef.current)
      {
        cursorTrackingCleanupRef.current();
        cursorTrackingCleanupRef.current = null;
      }
      
    }
    else if (rawAudioData && rawAudioData.length > 0)
    {
        processRawAudioData(rawAudioData);
    }
  };

  // Keep a live ref to the latest handler so the registered sink is never stale.
  const rawAudioHandlerRef = useRef(null);
  rawAudioHandlerRef.current = handleIncomingRawAudio;

  // Register a stable trampoline as the sink; it always calls the latest handler.
  useEffect(() => {
    if (!registerRawAudioSink) {
      return undefined;
    }
    const sink = (payload) => {
      if (rawAudioHandlerRef.current) {
        rawAudioHandlerRef.current(payload);
      }
    };
    registerRawAudioSink(sink);
    return () => registerRawAudioSink(null);
  }, [registerRawAudioSink]);

  // Initialize AudioContext for raw audio decoding
  const initializeAudioContext = () => {
    if (!audioContextRef.current)
    {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        return audioContext;
      } catch (error) {
        console.error('Failed to initialize AudioContext:', error);
        return null;
      }
    }
    return audioContextRef.current;
  };

  // Process raw MP4/M4A audio data to extract waveform
  const processRawAudioData = async (newRawData) => {
    // Accumulate raw data. decodeAudioData needs the WHOLE fMP4 file, so we keep a
    // growing copy - but we do NOT decode here on every chunk. decodeAudioData(full
    // file) is O(file); called per 256KB batch on a 50MB file that is O(n^2) and
    // saturates the CPU (which is what actually broke playback). Racing async decodes
    // also finish out of order, so a later smaller/failed decode would overwrite a
    // bigger one -> the waveform jumps between 5s and 1min. Instead we schedule a
    // debounced, single-flight decode below.
    const currentBuffer = rawDataBufferRef.current;
    const combinedLength = currentBuffer.length + newRawData.length;
    const newBuffer = new Uint8Array(combinedLength);
    newBuffer.set(currentBuffer, 0);
    newBuffer.set(new Uint8Array(newRawData), currentBuffer.length);
    rawDataBufferRef.current = newBuffer;

    scheduleWaveformDecode();
  };

  // Run the full-file decode at most once per interval, and never two at a time.
  const WAVEFORM_DECODE_INTERVAL_MS = 1500;
  const scheduleWaveformDecode = () => {
    if (waveformDecodeTimerRef.current !== null) {
      return; // a decode is already scheduled; it will pick up the latest buffer
    }
    const sinceLast = Date.now() - lastWaveformDecodeRef.current;
    const delay = Math.max(0, WAVEFORM_DECODE_INTERVAL_MS - sinceLast);
    waveformDecodeTimerRef.current = setTimeout(() => {
      waveformDecodeTimerRef.current = null;
      lastWaveformDecodeRef.current = Date.now();
      decodeWaveformNow();
    }, delay);
  };

  // Reset all incremental-decode / envelope state (new stream or explicit reset).
  const resetWaveformEnvelope = () => {
    accumulatedSamplesRef.current = new Float32Array(0);
    envInitSegmentRef.current = null;
    envDecodeCursorRef.current = 0;
    envBucketSizeRef.current = 0;
    envCarrySumSqRef.current = 0;
    envCarryCountRef.current = 0;
    envTimescaleRef.current = 0;
  };

  // Big-endian uint32 helpers for walking / patching fMP4 box headers.
  const readU32BE = (buf, off) =>
    ((buf[off] << 24) | (buf[off + 1] << 16) | (buf[off + 2] << 8) | buf[off + 3]) >>> 0;
  const writeU32BE = (buf, off, val) => {
    buf[off] = (val >>> 24) & 0xff;
    buf[off + 1] = (val >>> 16) & 0xff;
    buf[off + 2] = (val >>> 8) & 0xff;
    buf[off + 3] = val & 0xff;
  };
  const boxType = (buf, off) =>
    String.fromCharCode(buf[off + 4], buf[off + 5], buf[off + 6], buf[off + 7]);

  // Read the media timescale (units per second) from the init segment's mdhd box
  // (moov > trak > mdia > mdhd). This is how we convert a fragment's
  // baseMediaDecodeTime (tfdt) into real seconds, so every decoded window can be
  // placed on the timeline by its TRUE container time instead of by accumulated
  // decoded-sample counts (which drift). Returns 0 if not found.
  const parseMediaTimescale = (buf) => {
    let result = 0;
    const walk = (start, end) => {
      let o = start;
      while (o + 8 <= end && result === 0) {
        const size = readU32BE(buf, o);
        if (size < 8 || o + size > end) break;
        const type = boxType(buf, o);
        if (type === 'moov' || type === 'trak' || type === 'mdia') {
          walk(o + 8, o + size); // recurse into container boxes
        } else if (type === 'mdhd') {
          const version = buf[o + 8];
          result = version === 1 ? readU32BE(buf, o + 28) : readU32BE(buf, o + 20);
        }
        o += size;
      }
    };
    walk(0, buf.length);
    return result;
  };

  // Byte offset of the first 'moof' box - everything before it (ftyp + moov) is the
  // initialization segment. Returns -1 if no fragment has arrived yet.
  const findInitSegmentEnd = (buf, total) => {
    let o = 0;
    while (o + 8 <= total) {
      const size = readU32BE(buf, o);
      if (size < 8 || o + size > total) break;
      if (boxType(buf, o) === 'moof') return o;
      o += size;
    }
    return -1;
  };

  // Starting at a fragment boundary `from`, return the end offset of a run of
  // COMPLETE fragments (each = moof + mdat) that stays within maxBytes. The window
  // always ends right after an mdat, so a whole number of fragments is decoded and a
  // moof is never split from its mdat.
  const nextFragmentWindowEnd = (buf, total, from, maxBytes) => {
    let o = from;
    let windowEnd = from;
    while (o + 8 <= total) {
      const size = readU32BE(buf, o);
      if (size < 8 || o + size > total) break; // incomplete tail box
      const type = boxType(buf, o);
      o += size;
      if (type === 'mdat') {
        windowEnd = o; // a fragment completed here
        if (windowEnd - from >= maxBytes) break;
      }
    }
    return windowEnd;
  };

  // Rebase every tfdt (baseMediaDecodeTime) in a freshly-copied fragment window so
  // the first fragment starts at time 0. Without this, decodeAudioData on a
  // mid-stream fragment (large baseMediaDecodeTime) could allocate a huge buffer of
  // leading silence; after rebasing, the window decodes to exactly its own audio -
  // identical to the known-good "decode from the very start" case.
  // Returns the ORIGINAL baseMediaDecodeTime of the window's first fragment (in
  // media-timescale units) so the caller can position this window's envelope on the
  // global timeline by its true container time. Returns 0 if no tfdt is present.
  const rebaseFragmentWindowTfdt = (win) => {
    const tfdtOffsets = [];
    const walk = (start, end) => {
      let o = start;
      while (o + 8 <= end) {
        const size = readU32BE(win, o);
        if (size < 8 || o + size > end) break;
        const type = boxType(win, o);
        if (type === 'moof' || type === 'traf') {
          walk(o + 8, o + size); // recurse into container boxes
        } else if (type === 'tfdt') {
          tfdtOffsets.push(o);
        }
        o += size;
      }
    };
    walk(0, win.length);
    if (tfdtOffsets.length === 0) return 0;
    const readTfdt = (p) => {
      const version = win[p + 8];
      if (version === 1) {
        return readU32BE(win, p + 12) * 4294967296 + readU32BE(win, p + 16);
      }
      return readU32BE(win, p + 12);
    };
    const base0 = readTfdt(tfdtOffsets[0]);
    if (base0 === 0) return 0; // already starts at 0 (e.g. the first-ever window)
    for (const p of tfdtOffsets) {
      const version = win[p + 8];
      const rebased = readTfdt(p) - base0; // small: a window spans only seconds
      if (version === 1) {
        writeU32BE(win, p + 12, 0);
        writeU32BE(win, p + 16, rebased >>> 0);
      } else {
        writeU32BE(win, p + 12, rebased >>> 0);
      }
    }
    return base0;
  };

  // Downsample a decoded window into RMS envelope buckets and write them onto the
  // stored envelope at startBucket (= the window's true container time * rate). The
  // envelope index therefore always equals real time * WAVEFORM_ENVELOPE_RATE, so it
  // stays aligned with <audio>.currentTime no matter how the per-window decode length
  // rounds. Overlapping/adjacent windows simply overwrite, which self-corrects any
  // sub-bucket boundary drift; a real gap (should not happen for a continuous
  // recording) is left as silence.
  const appendEnvelopeFromAudioBuffer = (audioBuffer, startBucket) => {
    if (!envBucketSizeRef.current) {
      envBucketSizeRef.current = Math.max(1, Math.round(audioBuffer.sampleRate / WAVEFORM_ENVELOPE_RATE));
    }
    const bucketSize = envBucketSizeRef.current;
    const ch = audioBuffer.getChannelData(0); // first channel
    const start = Math.max(0, Math.round(startBucket));
    const newBuckets = [];
    let sumSq = 0;
    let count = 0;
    for (let i = 0; i < ch.length; i++) {
      const s = ch[i];
      sumSq += s * s;
      count++;
      if (count >= bucketSize) {
        newBuckets.push(Math.sqrt(sumSq / count));
        sumSq = 0;
        count = 0;
      }
    }
    if (count > 0) newBuckets.push(Math.sqrt(sumSq / count)); // trailing partial = growing edge
    if (newBuckets.length === 0) return 0;
    const needLen = start + newBuckets.length;
    const old = accumulatedSamplesRef.current;
    if (needLen > old.length) {
      const merged = new Float32Array(needLen);
      merged.set(old, 0);
      for (let i = 0; i < newBuckets.length; i++) merged[start + i] = newBuckets[i];
      accumulatedSamplesRef.current = merged;
    } else {
      for (let i = 0; i < newBuckets.length; i++) old[start + i] = newBuckets[i];
    }
    return newBuckets.length;
  };

  // Read the baseMediaDecodeTime (tfdt, in timescale units) of the moof at moofOff.
  const readMoofBaseTime = (buf, moofOff, moofEnd) => {
    let o = moofOff + 8; // into moof children
    while (o + 8 <= moofEnd) {
      const size = readU32BE(buf, o);
      if (size < 8 || o + size > moofEnd) break;
      const type = boxType(buf, o);
      if (type === 'traf') {
        let p = o + 8;
        const trafEnd = o + size;
        while (p + 8 <= trafEnd) {
          const s2 = readU32BE(buf, p);
          if (s2 < 8 || p + s2 > trafEnd) break;
          if (boxType(buf, p) === 'tfdt') {
            const version = buf[p + 8];
            return version === 1
              ? readU32BE(buf, p + 12) * 4294967296 + readU32BE(buf, p + 16)
              : readU32BE(buf, p + 12);
          }
          p += s2;
        }
      }
      o += size;
    }
    return 0;
  };

  // Decode raw PCM (mono mix) for the time range [startSec, endSec] by locating the
  // fMP4 fragments that cover it in the raw download and decoding just those. Returns
  // { samples: Float32Array, sampleRate } or null if the range isn't downloaded yet.
  const decodePcmForRange = async (startSec, endSec) => {
    const buf = rawDataBufferRef.current;
    const total = buf.length;
    if (total === 0) return null;
    const initEnd = envInitSegmentRef.current
      ? envInitSegmentRef.current.length
      : findInitSegmentEnd(buf, total);
    if (initEnd <= 0) return null;
    const init = envInitSegmentRef.current || buf.slice(0, initEnd);
    const ts = envTimescaleRef.current || parseMediaTimescale(init) || WAVEFORM_ENVELOPE_RATE;

    // Index moof fragments (byteOffset + start time) up to the available bytes.
    const frags = [];
    let o = initEnd;
    let limit = total;
    // Walk top-level boxes: record moof positions, stop media at a trailing mfra.
    while (o + 8 <= total) {
      const size = readU32BE(buf, o);
      if (size < 8 || o + size > total) break;
      const type = boxType(buf, o);
      if (type === 'mfra') { limit = o; break; }
      if (type === 'moof') {
        frags.push({ byteOffset: o, startTime: readMoofBaseTime(buf, o, o + size) / ts });
      }
      o += size;
    }
    if (frags.length === 0) return null;

    // First fragment with startTime <= startSec, and the fragment boundary just past endSec.
    let startIdx = 0;
    for (let i = 0; i < frags.length; i++) {
      if (frags[i].startTime <= startSec) startIdx = i; else break;
    }
    let endByte = limit;
    for (let i = startIdx + 1; i < frags.length; i++) {
      if (frags[i].startTime >= endSec) { endByte = frags[i].byteOffset; break; }
    }
    const fragStartByte = frags[startIdx].byteOffset;
    const fragStartSec = frags[startIdx].startTime;

    const clip = new Uint8Array(init.length + (endByte - fragStartByte));
    clip.set(init, 0);
    clip.set(buf.subarray(fragStartByte, endByte), init.length);
    rebaseFragmentWindowTfdt(clip.subarray(init.length));

    // Decode at the file's NATIVE sample rate (mdhd timescale) via an OfflineAudioContext,
    // so the shared AudioContext's rate can't force a resample -- downsampling a full-scale
    // transient (a loud knock) otherwise aliases into audible distortion, even though the
    // native MediaSource playback (at the file's own rate) is clean.
    const nativeRate = (envTimescaleRef.current >= 8000 && envTimescaleRef.current <= 384000)
      ? envTimescaleRef.current : 0;
    let audioBuffer;
    try {
      if (nativeRate && typeof OfflineAudioContext !== 'undefined') {
        const offline = new OfflineAudioContext(1, 1, nativeRate);
        audioBuffer = await offline.decodeAudioData(clip.buffer);
      } else {
        const audioContext = initializeAudioContext();
        if (!audioContext) return null;
        audioBuffer = await audioContext.decodeAudioData(clip.buffer);
      }
    } catch (err) {
      return null;
    }

    const sr = audioBuffer.sampleRate;
    const from = Math.max(0, Math.floor((startSec - fragStartSec) * sr));
    const to = Math.min(audioBuffer.length, Math.ceil((endSec - fragStartSec) * sr));
    const n = Math.max(0, to - from);
    if (n === 0) return null;

    // Per-channel slices (for faithful export) + a mono mix (for analysis).
    const channels = [];
    for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
      const data = audioBuffer.getChannelData(c);
      const ch = new Float32Array(n);
      for (let i = 0; i < n; i++) ch[i] = data[from + i];
      channels.push(ch);
    }
    const mono = new Float32Array(n);
    for (const ch of channels) for (let i = 0; i < n; i++) mono[i] += ch[i];
    if (channels.length > 1) for (let i = 0; i < n; i++) mono[i] /= channels.length;
    return { samples: mono, channels, sampleRate: sr };
  };

  // In-place iterative radix-2 FFT (Cooley-Tukey). re/im are length = power of 2.
  const fftRadix2 = (re, im) => {
    const n = re.length;
    for (let i = 1, j = 0; i < n; i++) {
      let bit = n >> 1;
      for (; j & bit; bit >>= 1) j ^= bit;
      j ^= bit;
      if (i < j) {
        const tr = re[i]; re[i] = re[j]; re[j] = tr;
        const ti = im[i]; im[i] = im[j]; im[j] = ti;
      }
    }
    for (let len = 2; len <= n; len <<= 1) {
      const ang = -2 * Math.PI / len;
      const wr = Math.cos(ang);
      const wi = Math.sin(ang);
      for (let i = 0; i < n; i += len) {
        let cr = 1, ci = 0;
        for (let k = 0; k < len / 2; k++) {
          const ur = re[i + k];
          const ui = im[i + k];
          const vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
          const vi = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
          re[i + k] = ur + vr;
          im[i + k] = ui + vi;
          re[i + k + len / 2] = ur - vr;
          im[i + k + len / 2] = ui - vi;
          const ncr = cr * wr - ci * wi;
          ci = cr * wi + ci * wr;
          cr = ncr;
        }
      }
    }
  };

  // Averaged magnitude spectrum (Welch's method: Hann-windowed 50%-overlap blocks).
  // Returns both the average and the per-bin peak-hold across blocks.
  const computeSpectrum = (samples, sampleRate) => {
    const N = 4096;
    if (samples.length < N) return null;
    const hann = new Float32Array(N);
    for (let i = 0; i < N; i++) hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
    const half = N / 2;
    const acc = new Float64Array(half);
    const peak = new Float64Array(half);
    let blocks = 0;
    const hop = N / 2;
    for (let off = 0; off + N <= samples.length; off += hop) {
      const re = new Float64Array(N);
      const im = new Float64Array(N);
      for (let i = 0; i < N; i++) re[i] = samples[off + i] * hann[i];
      fftRadix2(re, im);
      for (let k = 0; k < half; k++) {
        const m = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
        acc[k] += m;
        if (m > peak[k]) peak[k] = m;
      }
      blocks++;
    }
    if (blocks === 0) return null;
    const avg = new Float32Array(half);
    const peakMags = new Float32Array(half);
    let peakK = 1;
    for (let k = 0; k < half; k++) {
      avg[k] = acc[k] / blocks;
      peakMags[k] = peak[k];
      if (k > 0 && avg[k] > avg[peakK]) peakK = k;
    }
    const binHz = sampleRate / N;
    return { avg, peak: peakMags, binHz, sampleRate, peakHz: peakK * binHz };
  };

  // Level / statistics over a mono PCM selection.
  const computeStats = (samples, sampleRate) => {
    const n = samples.length;
    if (n === 0) return null;
    let sumSq = 0, peak = 0, dc = 0, clipped = 0, zc = 0;
    let prev = samples[0];
    for (let i = 0; i < n; i++) {
      const s = samples[i];
      sumSq += s * s;
      dc += s;
      const a = Math.abs(s);
      if (a > peak) peak = a;
      if (a >= 0.999) clipped++;
      if ((s >= 0) !== (prev >= 0)) zc++;
      prev = s;
    }
    const rms = Math.sqrt(sumSq / n);
    const toDb = (v) => (v > 0 ? 20 * Math.log10(v) : -Infinity);
    return {
      durationSec: n / sampleRate,
      samples: n,
      peakDb: toDb(peak),
      rmsDb: toDb(rms),
      crestDb: toDb(peak) - toDb(rms),
      dcOffset: dc / n,
      clipped,
      zcrHz: (zc / 2) * (sampleRate / n) // zero-crossings per second / 2 ~ dominant rate
    };
  };

  // Fundamental-frequency estimate via normalized autocorrelation (good for tones,
  // returns null for broadband/noisy selections like knocks).
  const detectNote = (samples, sampleRate) => {
    const minHz = 40, maxHz = 2000;
    const minLag = Math.floor(sampleRate / maxHz);
    const maxLag = Math.min(Math.floor(sampleRate / minHz), samples.length - 1);
    if (maxLag <= minLag) return null;
    let energy = 0;
    for (let i = 0; i < samples.length; i++) energy += samples[i] * samples[i];
    if (energy <= 0) return null;
    let bestLag = -1, bestVal = 0;
    for (let lag = minLag; lag <= maxLag; lag++) {
      let corr = 0;
      for (let i = 0; i + lag < samples.length; i++) corr += samples[i] * samples[i + lag];
      const norm = corr / energy;
      if (norm > bestVal) { bestVal = norm; bestLag = lag; }
    }
    if (bestLag < 0 || bestVal < 0.5) return null; // weak periodicity -> not tonal
    const freq = sampleRate / bestLag;
    const noteNum = Math.round(12 * Math.log2(freq / 440) + 69);
    const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const name = names[((noteNum % 12) + 12) % 12] + (Math.floor(noteNum / 12) - 1);
    const refFreq = 440 * Math.pow(2, (noteNum - 69) / 12);
    const cents = Math.round(1200 * Math.log2(freq / refFreq));
    return { freq, name, cents, confidence: bestVal };
  };

  // STFT magnitude frames for the spectrogram (time x frequency, in dB).
  const computeSpectrogram = (samples, sampleRate) => {
    const N = 1024;
    if (samples.length < N) return null;
    const hann = new Float32Array(N);
    for (let i = 0; i < N; i++) hann[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));
    const half = N / 2;
    const hop = N / 4;
    const frames = [];
    let maxDb = -Infinity;
    for (let off = 0; off + N <= samples.length; off += hop) {
      const re = new Float64Array(N);
      const im = new Float64Array(N);
      for (let i = 0; i < N; i++) re[i] = samples[off + i] * hann[i];
      fftRadix2(re, im);
      const col = new Float32Array(half);
      for (let k = 0; k < half; k++) {
        const m = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
        const db = m > 0 ? 20 * Math.log10(m) : -160;
        col[k] = db;
        if (db > maxDb) maxDb = db;
      }
      frames.push(col);
    }
    if (frames.length === 0) return null;
    return { frames, bins: half, binHz: sampleRate / N, maxDb, hopSec: hop / sampleRate };
  };

  // Encode the selected region as a 32-bit float WAV Blob and download it. Float keeps
  // the exact decoded levels (these recordings can sit near -60 dBFS, where 16-bit PCM
  // would quantize to a few bits and sound distorted).
  const exportSelectionWav = async () => {
    setContextMenu(null);
    const sel = selectionRef.current;
    if (!sel || sel.end - sel.start <= 0.001) return;
    const pcm = await decodePcmForRange(sel.start, sel.end);
    if (!pcm || !pcm.channels || pcm.channels.length === 0) return;
    const { channels, sampleRate } = pcm;
    const numCh = channels.length;
    const n = channels[0].length;

    const blockAlign = numCh * 4;
    const dataBytes = n * blockAlign;
    const buf = new ArrayBuffer(44 + dataBytes);
    const dv = new DataView(buf);
    const ws = (off, str) => { for (let i = 0; i < str.length; i++) dv.setUint8(off + i, str.charCodeAt(i)); };
    ws(0, 'RIFF'); dv.setUint32(4, 36 + dataBytes, true); ws(8, 'WAVE');
    // audioFormat 3 = IEEE float, 32 bits per sample.
    ws(12, 'fmt '); dv.setUint32(16, 16, true); dv.setUint16(20, 3, true); dv.setUint16(22, numCh, true);
    dv.setUint32(24, sampleRate, true); dv.setUint32(28, sampleRate * blockAlign, true);
    dv.setUint16(32, blockAlign, true); dv.setUint16(34, 32, true);
    ws(36, 'data'); dv.setUint32(40, dataBytes, true);
    let off = 44;
    for (let i = 0; i < n; i++) {
      for (let c = 0; c < numCh; c++) {
        dv.setFloat32(off, channels[c][i], true);
        off += 4;
      }
    }
    const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `selection_${formatClock(sel.start).replace(':', 'm')}s.wav`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Right-click "Analyze": decode PCM once, compute stats + spectrum + spectrogram + note.
  const analyzeSelection = async (tab) => {
    setContextMenu(null);
    const sel = selectionRef.current;
    if (!sel || sel.end - sel.start <= 0.001) return;
    if (tab) setAnalysisTab(tab);
    setAnalysisBusy(true);
    setAnalysis(null);
    try {
      const pcm = await decodePcmForRange(sel.start, sel.end);
      if (!pcm) {
        setAnalysis({ error: 'Could not decode this region (not downloaded yet or too short).' });
        return;
      }
      const spec = computeSpectrum(pcm.samples, pcm.sampleRate);
      if (!spec) {
        setAnalysis({ error: 'Selection is too short to analyze (need ~0.1 s).' });
        return;
      }
      const stats = computeStats(pcm.samples, pcm.sampleRate);
      const note = detectNote(pcm.samples, pcm.sampleRate);
      const spectrogram = computeSpectrogram(pcm.samples, pcm.sampleRate);
      setAnalysis({ spec, stats, note, spectrogram, sampleRate: pcm.sampleRate, startSec: sel.start, endSec: sel.end });
    } finally {
      setAnalysisBusy(false);
    }
  };

  // Pointer readout over the spectrum canvas: map x -> frequency, read nearest bin dB.
  const handleSpectrumHover = (event) => {
    if (!analysis || !analysis.spec) return;
    const canvas = spectrumCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const W = canvas.width;
    const padL = 46, padR = 12;
    const plotW = W - padL - padR;
    const x = ((event.clientX - rect.left) / rect.width) * W;
    if (x < padL || x > W - padR) { setSpecCursor(null); return; }
    const { avg, peak, binHz, sampleRate } = analysis.spec;
    const mags = specMode === 'peak' ? peak : avg;
    const fMax = sampleRate / 2;
    const fMin = 20;
    let f;
    if (specScale === 'log') {
      const logMin = Math.log10(fMin), logMax = Math.log10(fMax);
      f = Math.pow(10, logMin + ((x - padL) / plotW) * (logMax - logMin));
    } else {
      f = ((x - padL) / plotW) * fMax;
    }
    const k = Math.max(1, Math.min(mags.length - 1, Math.round(f / binHz)));
    let maxMag = 1e-12;
    for (let i = 1; i < mags.length; i++) if (mags[i] > maxMag) maxMag = mags[i];
    const db = 20 * Math.log10(mags[k] / maxMag);
    setSpecCursor({ f: k * binHz, db });
  };

  // Scan the RMS envelope for onset transients (sharp energy rises) and mark them.
  // Thresholds are derived from the recording's own statistics, so it works
  // regardless of absolute level. If a region is selected, only that range is scanned.
  const detectTransients = () => {
    const env = accumulatedSamplesRef.current;
    if (!env || env.length < 10) { setTransients([]); return; }
    const rate = WAVEFORM_ENVELOPE_RATE;

    const sel = selectionRef.current;
    const lo = sel && sel.end > sel.start ? Math.max(1, Math.floor(sel.start * rate)) : 1;
    const hi = sel && sel.end > sel.start ? Math.min(env.length - 1, Math.ceil(sel.end * rate)) : env.length - 1;
    if (hi - lo < 3) { setTransients([]); return; }

    // Onset strength = rectified rise of the envelope; peak of the scanned range.
    let maxEnv = 0;
    for (let i = lo; i <= hi; i++) if (env[i] > maxEnv) maxEnv = env[i];
    if (maxEnv <= 0) { setTransients([]); return; }
    const flux = new Float32Array(env.length);
    for (let i = lo; i <= hi; i++) {
      const d = env[i] - env[i - 1];
      flux[i] = d > 0 ? d : 0;
    }

    // Adaptive threshold: mean + k·std of the onset strength (level-independent).
    let mean = 0;
    for (let i = lo; i <= hi; i++) mean += flux[i];
    mean /= (hi - lo + 1);
    let variance = 0;
    for (let i = lo; i <= hi; i++) { const dv = flux[i] - mean; variance += dv * dv; }
    const std = Math.sqrt(variance / (hi - lo + 1));
    const thresh = mean + 2.5 * std;

    const minGap = 0.05; // seconds between onsets
    const found = [];
    let lastOnset = -Infinity;
    for (let i = Math.max(lo, 2); i < hi; i++) {
      const t = i / rate;
      // Local peak in onset strength, above threshold, and loud enough vs. the loudest point.
      if (flux[i] > thresh && flux[i] >= flux[i - 1] && flux[i] >= flux[i + 1]
          && env[i] > 0.12 * maxEnv && t - lastOnset > minGap) {
        found.push(Number(t.toFixed(3)));
        lastOnset = t;
      }
    }
    setTransients(found);
  };

  // Decode budget per fragment window. Keeps each decodeAudioData call small and
  // memory-bounded (a few MB of compressed audio -> at most tens of MB of PCM), so
  // multi-hour recordings decode incrementally over many passes instead of trying
  // to decode the whole file at once (which overflows the decoder and froze the
  // waveform at ~100 min).
  const MAX_DECODE_WINDOW_BYTES = 4 * 1024 * 1024;

  const decodeWaveformNow = async () => {
    // Single-flight: if a decode is already running, remember to run once more after.
    if (waveformDecodingRef.current) {
      waveformDecodePendingRef.current = true;
      return;
    }
    const audioContext = initializeAudioContext();
    if (!audioContext || rawDataBufferRef.current.length === 0) {
      return;
    }

    waveformDecodingRef.current = true;
    const decodedFromBytes = rawDataBufferRef.current.length;
    let lastAudioBuffer = null;
    let appendedAny = false;
    try {
      // Capture the init segment (ftyp+moov) once the first moof has arrived.
      if (envInitSegmentRef.current === null) {
        const buf = rawDataBufferRef.current;
        const initEnd = findInitSegmentEnd(buf, buf.length);
        if (initEnd <= 0) {
          // No decodable fragment yet - show a progress placeholder and wait.
          if (!hasDecodedAnyDataRef.current) {
            drawPlaceholderWaveform(buf.length);
          }
          return;
        }
        envInitSegmentRef.current = buf.slice(0, initEnd);
        envDecodeCursorRef.current = initEnd;
        envTimescaleRef.current = parseMediaTimescale(envInitSegmentRef.current);
      }

      const init = envInitSegmentRef.current;

      // Decode forward, one bounded fragment window at a time, until every complete
      // fragment currently available has been enveloped. New bytes that arrive while
      // we await are picked up because we re-read rawDataBufferRef each iteration.
      while (true) {
        const buf = rawDataBufferRef.current;
        const windowEnd = nextFragmentWindowEnd(
          buf, buf.length, envDecodeCursorRef.current, MAX_DECODE_WINDOW_BYTES
        );
        if (windowEnd <= envDecodeCursorRef.current) {
          break; // no complete fragment beyond the cursor yet
        }

        // Copy [init | fragments] into one standalone, decodable fMP4 buffer and
        // rebase its timestamps so it decodes to just this window's audio.
        const fragLen = windowEnd - envDecodeCursorRef.current;
        const clip = new Uint8Array(init.length + fragLen);
        clip.set(init, 0);
        clip.set(buf.subarray(envDecodeCursorRef.current, windowEnd), init.length);
        const windowBaseTime = rebaseFragmentWindowTfdt(clip.subarray(init.length));

        let audioBuffer;
        try {
          audioBuffer = await audioContext.decodeAudioData(clip.buffer);
        } catch (err) {
          // This window isn't cleanly decodable yet (rare box/codec hiccup). Stop and
          // retry on the next pass once more data has arrived; do NOT advance cursor.
          break;
        }

        // Position this window's envelope by its TRUE container time so the envelope
        // index stays == real time * rate (no cross-window decode drift). The media
        // timescale is normally the sample rate; fall back to the decoded rate.
        const timescale = envTimescaleRef.current || audioBuffer.sampleRate;
        const startBucket = (windowBaseTime / timescale) * WAVEFORM_ENVELOPE_RATE;
        appendEnvelopeFromAudioBuffer(audioBuffer, startBucket);
        envDecodeCursorRef.current = windowEnd;
        lastAudioBuffer = audioBuffer;
        appendedAny = true;

        // Draw progressively so the waveform visibly fills during catch-up.
        drawAccumulatedWaveform();
      }

      if (appendedAny) {
        hasDecodedAnyDataRef.current = true;

        // Force cursor redraw with the updated duration as the waveform grows.
        if (audioElement || audioElementRef.current) {
          setTimeout(() => drawPlaybackCursor(), 50);
        }

        // Try to find and connect to the audio element for cursor tracking.
        if (audioUrl && !audioElement) {
          setTimeout(() => {
            const foundAudio = document.querySelector(`audio[src="${audioUrl}"]`) ||
                             Array.from(document.querySelectorAll('audio')).find(el => el.src === audioUrl);
            if (foundAudio && foundAudio !== audioElement) {
              setAudioElement(foundAudio);
              if (cursorTrackingCleanupRef.current) {
                cursorTrackingCleanupRef.current();
              }
              cursorTrackingCleanupRef.current = setupPlaybackCursorTracking(foundAudio);
            }
          }, 50);
        }

        setIsReady(true);
        if (onReady && lastAudioBuffer) onReady(lastAudioBuffer);
      } else if (!hasDecodedAnyDataRef.current) {
        drawPlaceholderWaveform(rawDataBufferRef.current.length);
      }
    } finally {
      waveformDecodingRef.current = false;
      // If more bytes arrived during this decode (or a request came in), decode again
      // so the waveform catches up to the latest data and the final complete file.
      if (waveformDecodePendingRef.current || rawDataBufferRef.current.length > decodedFromBytes) {
        waveformDecodePendingRef.current = false;
        scheduleWaveformDecode();
      }
    }
  };

  // Draw a placeholder waveform showing download progress
  const drawPlaceholderWaveform = (bytesReceived) => {
    if (!waveformRef.current) return;

    // Create or get canvas
    let canvas = waveformRef.current.querySelector('canvas');
    if (!canvas)
    {
      canvas = document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.height = '192px';
      canvas.style.background = '#f8f9fa';
      waveformRef.current.innerHTML = '';
      waveformRef.current.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size to match display size
    canvas.width = rect.width;
    canvas.height = 192;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw center line
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, canvas.height / 2);
    ctx.lineTo(canvas.width, canvas.height / 2);
    ctx.stroke();

    // Draw download progress bar
    const estimatedFileSize = 5 * 1024 * 1024; // Estimate 5MB
    const progress = Math.min(bytesReceived / estimatedFileSize, 1.0);
    const progressWidth = canvas.width * progress;
    
    ctx.fillStyle = '#e3f2fd';
    ctx.fillRect(0, canvas.height / 2 - 10, progressWidth, 20);
    
    ctx.strokeStyle = '#2196f3';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, canvas.height / 2 - 10, progressWidth, 20);
    
    // Draw progress text
    ctx.fillStyle = '#666';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Downloading... ${(bytesReceived / 1024 / 1024).toFixed(1)}MB`, canvas.width / 2, canvas.height / 2 + 5);
  };

  
  // Initialize Web Audio API when audioUrl is available (for cursor tracking only)
  useEffect(() => {
    if (audioUrl)
    {
      // Clear accumulated samples for new stream
      resetWaveformEnvelope();
      hasDecodedAnyDataRef.current = false;
      
      // Clear previous audio element connection when we get a new URL
      setAudioElement(null);
      audioElementRef.current = null;
      
      // Clean up Web Audio API connections for the old audio element
      if (mediaElementSourceRef.current)
      {
        try {
          mediaElementSourceRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        mediaElementSourceRef.current = null;
      }
      
      if (gainNodeRef.current)
      {
        try {
          gainNodeRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        gainNodeRef.current = null;
      }
      
      // Clear the connected audio element reference
      connectedAudioElementRef.current = null;
      
      // Stop any ongoing cursor animation from previous audio
      if (cursorAnimationRef.current)
      {
        cancelAnimationFrame(cursorAnimationRef.current);
        cursorAnimationRef.current = null;
      }
      
      // Find the audio element that's using this URL
      const findAudioElement = () => {
        //console.log('WaveForm: Looking for audio element...');
        
        // Wait a bit more for the DOM to be ready
        const checkForAudio = () => {
          // Try multiple selectors to find the audio element
          let audio = document.querySelector(`audio[src="${audioUrl}"]`);
          if (!audio) {
            // Try finding any audio element and check if src matches
            const allAudio = document.querySelectorAll('audio');
            
            for (let audioEl of allAudio)
            {
              if (audioEl.src === audioUrl || audioEl.currentSrc === audioUrl)
              {
                audio = audioEl;
                break;
              }
            }
          }
          
          if (audio)
          {
            // Wait for audio to be ready before setting up controls
            const setupWhenReady = () => {
              if (audio.readyState >= 1)
              {
                //console.log('WaveForm: Audio element is ready, setting up controls');
                setAudioElement(audio);
                audioElementRef.current = audio; // Also store in ref
                
                // Setup Web Audio API gain control
                if (!audioContextRef.current)
                {
                  audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                
                // Only create MediaElementSource if this audio element hasn't been connected yet
                // Note: createMediaElementSource can only be called once per audio element
                if (connectedAudioElementRef.current !== audio) {
                  try {
                    mediaElementSourceRef.current = audioContextRef.current.createMediaElementSource(audio);
                    gainNodeRef.current = audioContextRef.current.createGain();
                    gainNodeRef.current.gain.value = gain;
                    // Optional preview filter between source and gain (allpass = flat/off).
                    filterNodeRef.current = audioContextRef.current.createBiquadFilter();
                    filterNodeRef.current.type = 'allpass';

                    // Connect: audio element -> filter -> gain node -> speakers
                    mediaElementSourceRef.current.connect(filterNodeRef.current);
                    filterNodeRef.current.connect(gainNodeRef.current);
                    gainNodeRef.current.connect(audioContextRef.current.destination);
                    
                    // Remember that this audio element is now connected
                    connectedAudioElementRef.current = audio;

                    // Safari keeps a newly created AudioContext 'suspended' until a
                    // user gesture resumes it. While suspended, Safari plays the
                    // <audio> element natively and bypasses the Web Audio graph, so
                    // the gain node has no effect (playback is heard, volume control
                    // does nothing). Resuming when playback starts - a user gesture -
                    // activates the graph so the gain takes effect. Attached once per
                    // element (this block only runs once per connected element).
                    audio.addEventListener('play', () => {
                      if (audioContextRef.current?.state === 'suspended') {
                        audioContextRef.current.resume().catch(() => {});
                      }
                    });
                    
                    console.log('WaveForm: Audio gain control initialized with gain:', gain, `(${(20 * Math.log10(gain)).toFixed(1)} dB)`);
                  } catch (error) {
                    console.warn('WaveForm: Could not setup gain control:', error);
                    // If createMediaElementSource fails (e.g., already created), audio will play directly
                  }
                } else {
                  // Audio element already connected, just update the gain value
                  if (gainNodeRef.current)
                  {
                    gainNodeRef.current.gain.value = gain;
                    console.log('WaveForm: Reusing existing gain control, updated gain to:', gain, `(${(20 * Math.log10(gain)).toFixed(1)} dB)`);
                  }
                }
                
                // Cleanup previous cursor tracking
                if (cursorTrackingCleanupRef.current)
                {
                  cursorTrackingCleanupRef.current();
                }
                
                // Set up playback cursor tracking and store cleanup function
                cursorTrackingCleanupRef.current = setupPlaybackCursorTracking(audio);
              } else {
                audio.addEventListener('loadedmetadata', setupWhenReady, { once: true });
                // Fallback timeout
                setTimeout(setupWhenReady, 2000);
              }
            };
            
            setupWhenReady();
            return true;
          }
          return false;
        };
        
        return checkForAudio();
      };
      
      // Try to find audio element with delays
      if (!findAudioElement()) {
        // If not found immediately, retry periodically with longer delays
        let retries = 0;
        const maxRetries = 30; // Increased retries
        const retryInterval = setInterval(() => {
          retries++;
          //console.log(`WaveForm: Retry ${retries}/${maxRetries} looking for audio element`);
          
          if (findAudioElement() || retries >= maxRetries) {
            clearInterval(retryInterval);
            if (retries >= maxRetries) {
              console.warn('WaveForm: Failed to find audio element after maximum retries');
            }
          }
        }, 500); // Longer intervals
        
        // Cleanup interval if component unmounts
        return () => clearInterval(retryInterval);
      }
    }
  }, [audioUrl]);

  // Update gain when gain state changes
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = gain;
    }
    // On Safari the AudioContext starts 'suspended' and, while suspended, the
    // <audio> element plays natively - bypassing the gain node entirely. Moving
    // the gain slider is a user gesture, so resume the context here to activate
    // the Web Audio graph and make the gain change audible.
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  }, [gain]);

  // Apply playback rate to the audio element.
  useEffect(() => {
    const el = audioElementRef.current || audioElement;
    if (el) el.playbackRate = playbackRate;
  }, [playbackRate, audioElement]);

  // Apply the preview filter (allpass = off/flat).
  useEffect(() => {
    const f = filterNodeRef.current;
    if (!f) return;
    if (filterType === 'off') {
      f.type = 'allpass';
    } else {
      f.type = filterType;
      f.frequency.value = filterFreq;
      f.Q.value = filterType === 'bandpass' ? 4 : 0.707;
    }
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  }, [filterType, filterFreq, audioElement]);

  // Resume AudioContext when returning to the tab — browsers auto-suspend it when the tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Setup playback cursor tracking
  const setupPlaybackCursorTracking = (audioEl) => {

    const updatePlaybackState = () => {
      const audioDuration = audioEl.duration;
      const audioTime = audioEl.currentTime;

      // Selection / loop enforcement drives the streamed <audio> element directly, so
      // it works on the growing MSE stream without a fully-decoded buffer.
      const sel = selectionRef.current;
      if (playSelectionRef.current && sel && sel.end - sel.start > 0.001) {
        if (audioTime >= sel.end - 0.02) {
          if (loopRef.current) {
            try { audioEl.currentTime = sel.start; } catch (e) { /* ignore */ }
          } else {
            safePause(audioEl);
            try { audioEl.currentTime = sel.end; } catch (e) { /* ignore */ }
            playSelectionRef.current = false;
          }
        }
      } else if (loopRef.current && (!sel || sel.end <= sel.start)) {
        const total = getTotalDuration();
        if (total > 0 && audioTime >= total - 0.05) {
          try { audioEl.currentTime = 0; } catch (e) { /* ignore */ }
        }
      }

      setCurrentTime(audioTime);
      
      // Handle Infinity duration by using decoded audio duration
      if (isFinite(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      } else if (accumulatedSamplesRef.current.length > 0) {
        const calculatedDuration = accumulatedSamplesRef.current.length / WAVEFORM_ENVELOPE_RATE;
        setDuration(calculatedDuration);
        //console.log('WaveForm: Using calculated duration from samples:', calculatedDuration);
      } else {
        setDuration(0);
      }
      
      setIsPlaying(!audioEl.paused);
    };

    const startCursorAnimation = () => {
      if (cursorAnimationRef.current) return; // prevent stacking multiple RAF loops
      const updateCursor = () => {
        // Find current audio element dynamically
        let currentAudio = audioEl;
        if (!currentAudio && audioUrl) {
          currentAudio = document.querySelector(`audio[src="${audioUrl}"]`);
          if (!currentAudio) {
            const allAudio = document.querySelectorAll('audio');
            for (let el of allAudio) {
              if (el.src === audioUrl) {
                currentAudio = el;
                break;
              }
            }
          }
        }
        
        if (currentAudio && !currentAudio.paused) {
          setCurrentTime(currentAudio.currentTime);
          drawPlaybackCursor();
        }
        cursorAnimationRef.current = requestAnimationFrame(updateCursor);
      };
      updateCursor();
    };

    const stopCursorAnimation = () => {
      if (cursorAnimationRef.current) {
        cancelAnimationFrame(cursorAnimationRef.current);
        cursorAnimationRef.current = null;
      }
    };

    // Event handler functions (store references for proper cleanup)
    const playHandler = () => {
      setIsPlaying(true);
      startCursorAnimation();
    };
    const pauseHandler = () => {
      setIsPlaying(false);
      stopCursorAnimation();
      drawPlaybackCursor(); // Draw final cursor position
    };
    const seekedHandler = () => {
      // IMPORTANT: Always update cursor position immediately after seek completes
      // This ensures cursor shows correct position regardless of playback state
      updatePlaybackState();
      drawPlaybackCursor();
    };
    const endedHandler = () => {
      // Loop the whole track back to the start when Loop is on and no region is set.
      if (loopRef.current && (!selectionRef.current || selectionRef.current.end <= selectionRef.current.start)) {
        try { audioEl.currentTime = 0; } catch (e) { /* ignore */ }
        safePlay(audioEl);
      }
    };

    // Event listeners
    audioEl.addEventListener('loadedmetadata', updatePlaybackState);
    audioEl.addEventListener('timeupdate', updatePlaybackState);
    audioEl.addEventListener('play', playHandler);
    audioEl.addEventListener('pause', pauseHandler);
    audioEl.addEventListener('seeked', seekedHandler);
    audioEl.addEventListener('durationchange', updatePlaybackState);
    audioEl.addEventListener('ended', endedHandler);

    // Initial state
    updatePlaybackState();
    
    // Cleanup function
    return () => {
      stopCursorAnimation();
      audioEl.removeEventListener('loadedmetadata', updatePlaybackState);
      audioEl.removeEventListener('timeupdate', updatePlaybackState);
      audioEl.removeEventListener('play', playHandler);
      audioEl.removeEventListener('pause', pauseHandler);
      audioEl.removeEventListener('seeked', seekedHandler);
      audioEl.removeEventListener('durationchange', updatePlaybackState);
      audioEl.removeEventListener('ended', endedHandler);
    };
  };

  // Draw complete accumulated waveform
  const drawAccumulatedWaveform = () => {
    if (!waveformRef.current) return;

    const audioData = accumulatedSamplesRef.current;
    if (audioData.length === 0) return;

    // Create or get canvas
    let canvas = waveformRef.current.querySelector('canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.width = '100%';
      canvas.style.height = '192px';
      canvas.style.background = '#f8f9fa';
      waveformRef.current.innerHTML = '';
      waveformRef.current.appendChild(canvas);
    }

    const rect = canvas.getBoundingClientRect();
    // Only resize when dimensions actually change — setting canvas.width always clears the canvas
    // Skip entirely if tab is hidden (rect.width === 0)
    if (rect.width === 0) return;
    if (canvas.width !== rect.width || canvas.height !== 192) {
      canvas.width = rect.width;
      canvas.height = 192;
    }

    // Cache canvas for cursor updates
    lastCanvasRef.current = canvas;
    
    // Attach the interactive controls once: drag to select a region, click to seek,
    // wheel to zoom/pan (Ctrl/Cmd = amplitude zoom, Shift/horizontal = pan).
    if (!canvas.hasAttribute('data-click-handler')) {
      canvas.setAttribute('data-click-handler', 'true');
      canvas.style.cursor = 'text';

      const mouseLeaveHandler = () => {
        canvas.title = ''; // Clear tooltip
        dragRef.current = null; // abandon an in-progress drag when leaving the canvas
      };

      canvas.addEventListener('mousedown', handleWaveformPointerDown);
      canvas.addEventListener('mousemove', handleWaveformPointerMove);
      canvas.addEventListener('mouseup', handleWaveformPointerUp);
      canvas.addEventListener('mouseleave', mouseLeaveHandler);
      canvas.addEventListener('wheel', handleWaveformWheel, { passive: false });
      canvas.addEventListener('contextmenu', handleWaveformContextMenu);

      // Store handlers for cleanup
      canvas._waveformHandlers = {
        mousedown: handleWaveformPointerDown,
        mousemove: handleWaveformPointerMove,
        mouseup: handleWaveformPointerUp,
        mouseleave: mouseLeaveHandler,
        wheel: handleWaveformWheel,
        contextmenu: handleWaveformContextMenu
      };

      //console.log('WaveForm: Interactive controls enabled on waveform');
    }
    
    // Use unified drawing function to draw waveform with cursor if available
    const showCursor = !!audioElement;
    const cursorTime = audioElement ? audioElement.currentTime : 0;
    
    drawCompleteWaveform({ 
      showCursor, 
      cursorTime 
    });
  };

  // NEW: Handle waveform hover for seek preview
  const handleWaveformHover = (event) => {
    // Use the same robust audio element detection
    let currentAudioElement = null;
    
    // First try: use the ref if available
    if (audioElementRef.current && audioElementRef.current.readyState >= 1) {
      currentAudioElement = audioElementRef.current;
    }
    
    // Second try: use state if ref not available
    if (!currentAudioElement && audioElement) {
      currentAudioElement = audioElement;
    }
    
    // Third try: search DOM
    if (!currentAudioElement && audioUrl) {
      currentAudioElement = document.querySelector(`audio[src="${audioUrl}"]`);
      if (!currentAudioElement) {
        const allAudio = document.querySelectorAll('audio');
        for (let audioEl of allAudio) {
          if (audioEl.src === audioUrl || audioEl.currentSrc === audioUrl) {
            currentAudioElement = audioEl;
            break;
          }
        }
      }
    }
    
    if (!currentAudioElement || !lastCanvasRef.current) return;

    const canvas = lastCanvasRef.current;
    const hoverTime = xToTime(event.clientX);

    // Update cursor style with time info
    const hoverMinutes = Math.floor(hoverTime / 60);
    const hoverSeconds = Math.floor(hoverTime % 60);
    const timeText = `${hoverMinutes}:${hoverSeconds.toString().padStart(2, '0')}`;
    canvas.title = `Click to seek to ${timeText}`;
  };

  // NEW: Handle waveform clicks for seeking
  const handleWaveformClick = (event) => {
    //console.log('WaveForm: Click detected on waveform');

    // Measure tool: a click records a time point instead of seeking.
    if (measureModeRef.current) {
      const t = xToTime(event.clientX);
      setMeasurePoints((pts) => (pts.length >= 2 ? [t] : [...pts, t]));
      return;
    }

    // Always search for audio element dynamically - don't rely on state
    let currentAudioElement = null;
    
    // First try: use the ref if available
    if (audioElementRef.current && audioElementRef.current.readyState >= 1) {
      currentAudioElement = audioElementRef.current;
      //console.log('WaveForm: Using audio element from ref');
    }
    
    // Second try: use state if ref not available
    if (!currentAudioElement && audioElement) {
      currentAudioElement = audioElement;
      //console.log('WaveForm: Using audio element from state');
    }
    
    // Third try: search DOM
    if (!currentAudioElement && audioUrl) {
      //console.log('WaveForm: Searching for audio element...', { audioUrl });
      
      // Direct selector match
      currentAudioElement = document.querySelector(`audio[src="${audioUrl}"]`);
      //console.log('WaveForm: Direct selector result:', !!currentAudioElement, currentAudioElement?.src);
      
      if (!currentAudioElement) {
        // Check all audio elements for src or currentSrc match
        const allAudio = document.querySelectorAll('audio');
        //console.log('WaveForm: Found', allAudio.length, 'audio elements');
        for (let audioEl of allAudio) {
          //console.log('WaveForm: Checking audio element:', {
          //  src: audioEl.src,
          //  currentSrc: audioEl.currentSrc,
          //  readyState: audioEl.readyState,
          //  paused: audioEl.paused,
          //  currentTime: audioEl.currentTime,
          //  duration: audioEl.duration
          //});
          
          if (audioEl.src === audioUrl || audioEl.currentSrc === audioUrl) {
            currentAudioElement = audioEl;
            //console.log('WaveForm: Found matching audio element via', audioEl.src === audioUrl ? 'src' : 'currentSrc');
            break;
          }
        }
      }
      
      // Fourth try: find any audio element that's currently playing or has loaded content
      if (!currentAudioElement) {
        //console.log('WaveForm: No URL match found, trying to find any active audio element');
        const allAudio = document.querySelectorAll('audio');
        for (let audioEl of allAudio) {
          if (audioEl.readyState >= 1 && (audioEl.duration > 0 || !isNaN(audioEl.duration))) {
            //console.log('WaveForm: Found active audio element (readyState >= 1):', {
            //  src: audioEl.src,
            //  currentSrc: audioEl.currentSrc,
            //  readyState: audioEl.readyState,
            //  duration: audioEl.duration
            //});
            currentAudioElement = audioEl;
            
            // Update our state and ref to match
            setAudioElement(currentAudioElement);
            audioElementRef.current = currentAudioElement;
            break;
          }
        }
      }
    }
    
    if (!currentAudioElement || !lastCanvasRef.current) {
      if (!audioUrl) {
        //console.log('WaveForm: Audio is being reset - click will be available when new audio loads');
      } else {
        console.warn('WaveForm: Cannot seek - no audio element found or no canvas');
        console.warn('Audio element (state):', !!audioElement, 'Audio element (ref):', !!audioElementRef.current, 'Canvas:', !!lastCanvasRef.current);
      }
      if (audioUrl) {
        console.warn('WaveForm: Expected audioUrl:', audioUrl);
        const allAudio = document.querySelectorAll('audio');
        console.warn('WaveForm: All audio elements in DOM:', Array.from(allAudio).map(el => ({
          src: el.src,
          currentSrc: el.currentSrc,
          readyState: el.readyState,
          id: el.id,
          className: el.className,
          paused: el.paused,
          currentTime: el.currentTime,
          duration: el.duration
        })));
      }
      return;
    }
    
    const canvas = lastCanvasRef.current;
    const view = getView();
    // Map the click into the current (possibly zoomed) view window.
    const targetTime = Math.max(0, Math.min(xToTime(event.clientX), view.total));
    // A plain click sets a fresh transport position, so drop any active selection.
    setSelection(null);
    playSelectionRef.current = false;
    
    // Call onTimeSelected callback if provided (for Comments component)
    if (onTimeSelected && typeof onTimeSelected === 'function') {
      onTimeSelected(targetTime);
      //console.log('WaveForm: Time selected callback triggered:', targetTime);
    }
    
    try {
      //console.log(`WaveForm: Attempting to seek to ${targetTime.toFixed(2)}s (${Math.floor(targetTime/60)}:${Math.floor(targetTime%60).toString().padStart(2, '0')})`);
      
      // Perform the seek
      currentAudioElement.currentTime = targetTime;
      
      // Update state immediately for responsive feedback
      setCurrentTime(targetTime);
      
      // Flash the waveform briefly to indicate the seek
      const originalOpacity = canvas.style.opacity || '1.0';
      canvas.style.opacity = '0.7';
      setTimeout(() => {
        canvas.style.opacity = originalOpacity;
      }, 150);
      
      //console.log('WaveForm: Seek completed successfully');
      
    } catch (error) {
      console.error('WaveForm: Seek failed:', error);
    }
  };

  // --- Interaction: drag-select, click-to-seek, wheel zoom/pan -------------------
  const handleWaveformPointerDown = (event) => {
    if (event.button !== 0) return; // left button only; right-click opens the context menu
    if (!lastCanvasRef.current) return;
    dragRef.current = { downX: event.clientX, downTime: xToTime(event.clientX), moved: false };
  };

  const handleWaveformPointerMove = (event) => {
    const drag = dragRef.current;
    if (drag) {
      if (Math.abs(event.clientX - drag.downX) > 3) drag.moved = true;
      if (drag.moved) {
        const t = xToTime(event.clientX);
        setSelection({ start: Math.min(drag.downTime, t), end: Math.max(drag.downTime, t) });
      }
      return;
    }
    // Not dragging -> show the hover seek-preview tooltip.
    handleWaveformHover(event);
  };

  const handleWaveformPointerUp = (event) => {
    if (event.button !== 0) return; // ignore right/middle button (context menu handles right-click)
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    // A click without a drag seeks (handleWaveformClick also clears the selection).
    if (!drag.moved) handleWaveformClick(event);
  };

  // Right-click over the waveform: if a region is selected, offer its spectrum.
  const handleWaveformContextMenu = (event) => {
    if (!selectionRef.current || selectionRef.current.end - selectionRef.current.start <= 0.001) return;
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY });
  };

  const handleWaveformWheel = (event) => {
    if (accumulatedSamplesRef.current.length === 0) return;
    event.preventDefault();
    // Ctrl/Cmd -> amplitude zoom.
    if (event.ctrlKey || event.metaKey) {
      setAmpScale(a => Math.max(AMP_MIN, Math.min(AMP_MAX, a * (event.deltaY < 0 ? 1.2 : 1 / 1.2))));
      return;
    }
    const view = getView();
    const span = view.end - view.start;
    // Shift or a horizontal wheel -> pan.
    if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      const delta = event.deltaX || event.deltaY;
      panTime((delta / 500) * span);
      return;
    }
    // Otherwise zoom in/out around the pointer.
    zoomTime(event.deltaY < 0 ? 1.2 : 1 / 1.2, xToTime(event.clientX));
  };

  // --- Zoom / amplitude / selection controls ------------------------------------
  // Encode "fully zoomed out" as viewEnd === 0 so the view keeps following the
  // growing recording while streaming.
  const commitView = (start, end, total) => {
    if (start <= 0.001 && end >= total - 0.001) {
      setViewStart(0);
      setViewEnd(0);
    } else {
      setViewStart(start);
      setViewEnd(end);
    }
  };

  const zoomTime = (factor, centerTime) => {
    const view = getView();
    const span = view.end - view.start;
    const total = view.total || span;
    const newSpan = Math.min(total, Math.max(0.05, span / factor));
    const c = centerTime != null ? centerTime : (view.start + view.end) / 2;
    const ratio = span > 0 ? (c - view.start) / span : 0.5;
    let start = c - ratio * newSpan;
    let end = start + newSpan;
    if (start < 0) { start = 0; end = newSpan; }
    if (end > total) { end = total; start = Math.max(0, total - newSpan); }
    commitView(start, end, total);
  };

  const panTime = (deltaSeconds) => {
    const view = getView();
    const span = view.end - view.start;
    const total = view.total;
    const start = Math.max(0, Math.min(view.start + deltaSeconds, Math.max(0, total - span)));
    commitView(start, start + span, total);
  };

  const zoomFit = () => { setViewStart(0); setViewEnd(0); };

  const zoomToSelection = () => {
    const sel = selectionRef.current;
    if (!sel || sel.end <= sel.start) return;
    const total = getTotalDuration();
    const pad = (sel.end - sel.start) * 0.05;
    commitView(Math.max(0, sel.start - pad), Math.min(total, sel.end + pad), total);
  };

  const changeAmp = (factor) => setAmpScale(a => Math.max(AMP_MIN, Math.min(AMP_MAX, a * factor)));

  const clearSelection = () => {
    setSelection(null);
    playSelectionRef.current = false;
  };

  // Play the selected region (looping if Loop is on). No-op fallback to normal play.
  const playSelectionRegion = () => {
    const audioEl = getAudioElement();
    if (!audioEl) return;
    const sel = selectionRef.current;
    if (sel && sel.end - sel.start > 0.001) {
      playSelectionRef.current = true;
      try { audioEl.currentTime = sel.start; } catch (e) { /* seek may fail pre-metadata */ }
    } else {
      playSelectionRef.current = false;
    }
    safePlay(audioEl);
  };

  // Stop: pause and return the transport to the very beginning.
  const handleStop = () => {
    const audioEl = getAudioElement();
    playSelectionRef.current = false;
    if (!audioEl) return;
    try {
      safePause(audioEl);
      audioEl.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    } catch (error) {
      console.error('Stop failed:', error);
    }
  };

  // --- Scrollbar drag helpers ----------------------------------------------------
  // Horizontal scrollbar pans the time view (only meaningful when zoomed in). The
  // thumb spans viewSpan/total and its left edge sits at viewStart/total.
  const beginHScrollDrag = (event) => {
    event.preventDefault();
    const track = event.currentTarget;
    const rect = track.getBoundingClientRect();
    const total = getTotalDuration();
    if (total <= 0 || rect.width <= 0) return;
    const view = getView();
    const span = view.end - view.start;
    if (span >= total - 0.001) return; // fully zoomed out: nothing to pan
    const thumbFrac = span / total;
    const thumbW = thumbFrac * rect.width;
    const travel = rect.width - thumbW;
    let thumbLeft = travel > 0 ? (view.start / (total - span)) * travel : 0;
    const localX = event.clientX - rect.left;
    let grab;
    if (localX < thumbLeft || localX > thumbLeft + thumbW) {
      thumbLeft = Math.max(0, Math.min(travel, localX - thumbW / 2));
      grab = thumbW / 2;
      const start = (thumbLeft / (travel || 1)) * (total - span);
      commitView(start, start + span, total);
    } else {
      grab = localX - thumbLeft;
    }
    const onMove = (ev) => {
      const tl = Math.max(0, Math.min(travel, (ev.clientX - rect.left) - grab));
      const start = (tl / (travel || 1)) * (total - span);
      commitView(start, start + span, total);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Vertical scrollbar pans the amplitude when amplitude-zoomed (peaks clip past the
  // top). ampOffset 1 = peaks at top (thumb at top), 0 = baseline at bottom.
  const beginVScrollDrag = (event) => {
    event.preventDefault();
    const track = event.currentTarget;
    const rect = track.getBoundingClientRect();
    if (rect.height <= 0) return;
    const maxY = 0.8 * ampRef.current;
    if (maxY <= 1.001) return; // no vertical overflow to scroll
    const thumbFrac = 1 / maxY;
    const thumbH = thumbFrac * rect.height;
    const travel = rect.height - thumbH;
    let thumbTop = (1 - ampOffsetRef.current) * travel;
    const localY = event.clientY - rect.top;
    let grab;
    if (localY < thumbTop || localY > thumbTop + thumbH) {
      thumbTop = Math.max(0, Math.min(travel, localY - thumbH / 2));
      grab = thumbH / 2;
      setAmpOffset(1 - thumbTop / (travel || 1));
    } else {
      grab = localY - thumbTop;
    }
    const onMove = (ev) => {
      const tt = Math.max(0, Math.min(travel, (ev.clientY - rect.top) - grab));
      setAmpOffset(1 - tt / (travel || 1));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // NEW: Draw playback cursor using unified drawing approach
  const drawPlaybackCursor = () => {
    const canvas = lastCanvasRef.current;
    if (!canvas) {
      //console.log('WaveForm: No canvas available for cursor drawing');
      return;
    }
    
    // Try to find audio element dynamically if we don't have it
    let currentAudioElement = audioElement;
    if (!currentAudioElement && audioUrl) {
      currentAudioElement = document.querySelector(`audio[src="${audioUrl}"]`);
      if (!currentAudioElement) {
        const allAudio = document.querySelectorAll('audio');
        for (let audioEl of allAudio) {
          if (audioEl.src === audioUrl || audioEl.currentSrc === audioUrl) {
            currentAudioElement = audioEl;
            break;
          }
        }
      }
      
      // Update our state if we found it
      if (currentAudioElement && currentAudioElement !== audioElement) {
        //console.log('WaveForm: Re-found audio element for cursor drawing');
        setAudioElement(currentAudioElement);
      }
    }
    
    if (!currentAudioElement) {
      //console.log('WaveForm: No audio element available for cursor drawing');
      return;
    }
    
    // Throttle cursor updates to reduce flickering
    const now = Date.now();
    if (now - lastDrawTimeRef.current < 50) { // Throttle to 20fps max
      return;
    }
    lastDrawTimeRef.current = now;
    
    // Use unified drawing function with cursor
    const timeToUse = currentAudioElement.currentTime >= 0 ? currentAudioElement.currentTime : currentTime;

    drawCompleteWaveform({ 
      showCursor: true, 
      cursorTime: timeToUse 
    });
  };

  // Periodically recover lost audio element reference (cursor drawing is handled by RAF loop)
  useEffect(() => {
    if (!audioUrl) return;
    const recoveryInterval = setInterval(() => {
      if (!audioElementRef.current) {
        const found = document.querySelector(`audio[src="${audioUrl}"]`) ||
          Array.from(document.querySelectorAll('audio')).find(
            el => el.src === audioUrl || el.currentSrc === audioUrl
          );
        if (found) {
          setAudioElement(found);
          audioElementRef.current = found;
        }
      }
    }, 500);
    return () => clearInterval(recoveryInterval);
  }, [audioUrl]);

  // Debug effect to track audio element and playback state
  useEffect(() => {
    if (audioElement) {
      // Force cursor redraw when audio element becomes available
      setTimeout(() => {
        if (lastCanvasRef.current && accumulatedSamplesRef.current.length > 0) {
          //console.log('WaveForm: Force redraw cursor after audio element detection');
          drawPlaybackCursor();
        }
      }, 100);
    } else {
      // Try to recover the audio element
      if (audioUrl) {
        const recovered = document.querySelector(`audio[src="${audioUrl}"]`) || 
                         Array.from(document.querySelectorAll('audio')).find(el => el.src === audioUrl || el.currentSrc === audioUrl);
        if (recovered) {
          setAudioElement(recovered);
        }
      }
    }
  }, [audioElement, audioUrl]);

  // Redraw waveform when comments change to update markers
  useEffect(() => {
    if (commentsRef.current && commentsRef.current.length >= 0 && accumulatedSamplesRef.current.length > 0)
    {
      // Use a slight delay to ensure all state has updated
      setTimeout(() => {
        drawAccumulatedWaveform();
      }, 10);
    }
  }, [comments]);

  // Draw the spectrum (log/linear frequency x, dB magnitude y) into the modal canvas.
  useEffect(() => {
    if (analysisTab !== 'spectrum' || !analysis || analysis.error || !analysis.spec) return;
    const canvas = spectrumCanvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0b1020';
    ctx.fillRect(0, 0, W, H);

    const padL = 46, padR = 12, padT = 12, padB = 26;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const { avg, peak, binHz, sampleRate, peakHz } = analysis.spec;
    const mags = specMode === 'peak' ? peak : avg;
    const nyquist = sampleRate / 2;
    const fMin = 20;
    const fMax = nyquist;
    const logMin = Math.log10(fMin);
    const logMax = Math.log10(fMax);
    const isLog = specScale === 'log';
    const xOf = (f) => isLog
      ? padL + ((Math.log10(Math.max(fMin, f)) - logMin) / (logMax - logMin)) * plotW
      : padL + (Math.max(0, f) / fMax) * plotW;

    let maxMag = 1e-12;
    for (let k = 1; k < mags.length; k++) if (mags[k] > maxMag) maxMag = mags[k];
    const dbMin = -100, dbMax = 0;
    const yOf = (db) => padT + (1 - (Math.max(dbMin, Math.min(dbMax, db)) - dbMin) / (dbMax - dbMin)) * plotH;

    // Grid: frequency ticks + dB lines.
    ctx.strokeStyle = '#233';
    ctx.fillStyle = '#7a8aa0';
    ctx.font = '10px sans-serif';
    ctx.lineWidth = 1;
    const freqTicks = isLog
      ? [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000]
      : [0, 2000, 4000, 6000, 8000, 10000, 12000, 16000, 20000];
    freqTicks.forEach((f) => {
      if (f > fMax) return;
      const x = xOf(f);
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, padT + plotH); ctx.stroke();
      ctx.textAlign = 'center';
      ctx.fillText(f >= 1000 ? `${f / 1000}k` : `${f}`, x, H - 8);
    });
    for (let db = 0; db >= dbMin; db -= 20) {
      const y = yOf(db);
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(padL + plotW, y); ctx.stroke();
      ctx.textAlign = 'right';
      ctx.fillText(`${db}`, padL - 6, y + 3);
    }

    // Harmonic markers from the dominant fundamental.
    if (specHarmonics && peakHz > 0) {
      ctx.strokeStyle = 'rgba(212,175,55,0.5)';
      ctx.fillStyle = 'rgba(212,175,55,0.9)';
      ctx.setLineDash([3, 3]);
      for (let h = 1; h <= 12; h++) {
        const f = peakHz * h;
        if (f > fMax) break;
        const x = xOf(f);
        ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, padT + plotH); ctx.stroke();
        if (h === 1) ctx.fillText('f₀', x, padT + 10);
      }
      ctx.setLineDash([]);
    }

    // Spectrum curve.
    ctx.strokeStyle = '#4ade80';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    let started = false;
    for (let k = 1; k < mags.length; k++) {
      const f = k * binHz;
      if (isLog && f < fMin) continue;
      const db = 20 * Math.log10(mags[k] / maxMag);
      const x = xOf(f);
      const y = yOf(db);
      if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
    }
    ctx.stroke();

    // Cursor readout line.
    if (specCursor) {
      const x = xOf(specCursor.f);
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, padT + plotH); ctx.stroke();
      ctx.fillStyle = '#e5e7eb';
      ctx.textAlign = x > W - 100 ? 'right' : 'left';
      const label = `${specCursor.f >= 1000 ? (specCursor.f / 1000).toFixed(2) + ' kHz' : Math.round(specCursor.f) + ' Hz'} · ${specCursor.db.toFixed(1)} dB`;
      ctx.fillText(label, x + (x > W - 100 ? -6 : 6), padT + 12);
    }
  }, [analysis, analysisTab, specScale, specMode, specHarmonics, specCursor]);

  // Draw the spectrogram (time x, frequency y, magnitude as color) into its canvas.
  useEffect(() => {
    if (analysisTab !== 'spectrogram' || !analysis || analysis.error || !analysis.spectrogram) return;
    const canvas = spectrogramCanvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);
    const { frames, bins, maxDb } = analysis.spectrogram;
    const nFrames = frames.length;
    const floorDb = maxDb - 80; // 80 dB dynamic range
    const img = ctx.createImageData(W, H);
    // Viridis-ish ramp: dark blue -> green -> yellow.
    const color = (t) => {
      t = Math.max(0, Math.min(1, t));
      const r = Math.round(255 * Math.min(1, Math.max(0, 1.5 * t - 0.3)));
      const g = Math.round(255 * Math.min(1, Math.max(0, 1.4 * t)));
      const b = Math.round(255 * Math.min(1, Math.max(0, 0.6 - t)));
      return [r, g, b];
    };
    for (let px = 0; px < W; px++) {
      const fi = Math.min(nFrames - 1, Math.floor((px / W) * nFrames));
      const col = frames[fi];
      for (let py = 0; py < H; py++) {
        const k = Math.min(bins - 1, Math.floor((1 - py / H) * bins));
        const db = col[k];
        const t = (db - floorDb) / (maxDb - floorDb);
        const [r, g, b] = color(t);
        const idx = (py * W + px) * 4;
        img.data[idx] = r; img.data[idx + 1] = g; img.data[idx + 2] = b; img.data[idx + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [analysis, analysisTab]);

  // Cleanup function
  useEffect(() => {
    return () => {
      // Stop animations
      if (cursorAnimationRef.current) {
        cancelAnimationFrame(cursorAnimationRef.current);
      }
      
      // Clean up canvas event listeners
      if (lastCanvasRef.current && lastCanvasRef.current._waveformHandlers) {
        const canvas = lastCanvasRef.current;
        const handlers = canvas._waveformHandlers;
        
        canvas.removeEventListener('mousedown', handlers.mousedown);
        canvas.removeEventListener('mousemove', handlers.mousemove);
        canvas.removeEventListener('mouseup', handlers.mouseup);
        canvas.removeEventListener('mouseleave', handlers.mouseleave);
        canvas.removeEventListener('wheel', handlers.wheel);
        if (handlers.contextmenu) canvas.removeEventListener('contextmenu', handlers.contextmenu);
        
        canvas.removeAttribute('data-click-handler');
        canvas._waveformHandlers = null;
      }
      
      // Close AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      
      // Clear accumulated data
      accumulatedSamplesRef.current = [];
      rawDataBufferRef.current = new Uint8Array(0);
      hasDecodedAnyDataRef.current = false;
      lastCanvasRef.current = null;
    };
  }, []);

  // Handle window resize to redraw waveform with correct dimensions
  useEffect(() => {
    const handleResize = () => {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        const canvas = lastCanvasRef.current;
        if (canvas && accumulatedSamplesRef.current.length > 0) {
          console.log('WaveForm: Window resized, redrawing waveform');
          
          // Update canvas dimensions
          const rect = canvas.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = 192;
          
          // Redraw the complete waveform with current cursor position
          const currentAudioElement = audioElementRef.current || audioElement;
          const showCursor = !!currentAudioElement;
          const cursorTime = currentAudioElement ? currentAudioElement.currentTime : currentTime;
          
          drawCompleteWaveform({ 
            showCursor, 
            cursorTime 
          });
        }
      }, 100); // 100ms debounce
    };

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [audioElement, currentTime]); // Dependencies for cursor state

  // Expose seeking functionality to parent component
  useEffect(() => {
    if (onCurrentTimeChange && typeof onCurrentTimeChange === 'function') {
      // Pass current time and seeking function to parent
      onCurrentTimeChange(currentTime, seekToTime);
    }
  }, [currentTime, onCurrentTimeChange]);

  // Redraw whenever the zoom window, amplitude, or selection changes so the canvas
  // (which is drawn imperatively) reflects the new view immediately.
  useEffect(() => {
    if (accumulatedSamplesRef.current.length > 0 && lastCanvasRef.current) {
      drawAccumulatedWaveform();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStart, viewEnd, ampScale, ampOffset, selection, transients, measurePoints]);

  // When zoomed in far enough, decode the visible range and overlay the real
  // per-sample waveform (debounced; padded so small pans reuse the same decode).
  useEffect(() => {
    const total = getTotalDuration();
    const vs = Math.max(0, viewStart);
    const ve = viewEnd > viewStart ? viewEnd : total;
    const span = ve - vs;
    if (span <= 0 || span > SAMPLE_VIEW_MAX_SEC || accumulatedSamplesRef.current.length === 0) {
      if (sampleOverlayRef.current) { sampleOverlayRef.current = null; drawAccumulatedWaveform(); }
      return;
    }
    const so = sampleOverlayRef.current;
    if (so && vs >= so.start - 0.005 && ve <= so.end + 0.005) return; // already covered
    if (sampleOverlayTimerRef.current) clearTimeout(sampleOverlayTimerRef.current);
    sampleOverlayTimerRef.current = setTimeout(async () => {
      const padStart = Math.max(0, vs - span * 0.25);
      const padEnd = ve + span * 0.25;
      const pcm = await decodePcmForRange(padStart, padEnd);
      if (pcm) {
        sampleOverlayRef.current = { start: padStart, end: padEnd, samples: pcm.samples, sampleRate: pcm.sampleRate };
        drawAccumulatedWaveform();
      }
    }, 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStart, viewEnd]);

  // NEW: Handle button-based playback controls
  const handlePlayPause = () => {
    const audioEl = getAudioElement();
    if (!audioEl) return;

    if (audioEl.paused) {
      // Audacity-style: if a region is selected, Play plays that region (and loops it
      // when Loop is on); otherwise it plays the whole track from the cursor.
      const sel = selectionRef.current;
      if (sel && sel.end - sel.start > 0.001) {
        playSelectionRef.current = true;
        if (audioEl.currentTime < sel.start || audioEl.currentTime >= sel.end - 0.02) {
          try { audioEl.currentTime = sel.start; } catch (e) { /* seek may fail pre-metadata */ }
        }
      } else {
        playSelectionRef.current = false;
      }
      safePlay(audioEl);
    } else {
      safePause(audioEl);
    }
  };

  const handleSeek = (seconds) => {
    const audioEl = getAudioElement();
    if (!audioEl) return;
    
    const currentTime = audioEl.currentTime;
    let seekDuration = duration;
    
    // Handle Infinity duration
    if (!isFinite(seekDuration) || seekDuration <= 0) {
      const audioData = accumulatedSamplesRef.current;
      if (audioData.length > 0) {
        seekDuration = audioData.length / WAVEFORM_ENVELOPE_RATE;
      }
    }
    
    if (seekDuration <= 0) return;
    
    const newTime = Math.max(0, Math.min(seekDuration, currentTime + seconds));
    try {
      audioEl.currentTime = newTime;
      setCurrentTime(newTime);
    } catch (error) {
      console.error('Button seek failed:', error);
    }
  };

  // Absolute seek to specific time position
  const seekToTime = (targetTimeSeconds) => {
    console.log('WaveForm: Seeking to absolute time:', targetTimeSeconds);
    const audioEl = getAudioElement();
    if (!audioEl) {
      console.warn('WaveForm: No audio element available for seeking');
      return false;
    }
    
    let seekDuration = duration;
    
    // Handle Infinity duration
    if (!isFinite(seekDuration) || seekDuration <= 0) {
      const audioData = accumulatedSamplesRef.current;
      if (audioData.length > 0) {
        seekDuration = audioData.length / WAVEFORM_ENVELOPE_RATE;
      }
    }
    
    if (seekDuration <= 0) {
      console.warn('WaveForm: No valid duration available for seeking');
      return false;
    }
    
    // Clamp to valid range
    const clampedTime = Math.max(0, Math.min(seekDuration, targetTimeSeconds));
    
    try {
      audioEl.currentTime = clampedTime;
      setCurrentTime(clampedTime);
      console.log('WaveForm: Successfully seeked to:', clampedTime);
      return true;
    } catch (error) {
      console.error('WaveForm: Absolute seek failed:', error);
      return false;
    }
  };

  const handleGoToStart = () => {
    const audioEl = getAudioElement();
    if (!audioEl) return;
    
    try {
      audioEl.currentTime = 0;
      setCurrentTime(0);
    } catch (error) {
      console.error('Go to start failed:', error);
    }
  };

  const handleGoToEnd = () => {
    const audioEl = getAudioElement();
    if (!audioEl) return;
    
    let seekDuration = duration;
    
    // Handle Infinity duration
    if (!isFinite(seekDuration) || seekDuration <= 0) {
      const audioData = accumulatedSamplesRef.current;
      if (audioData.length > 0) {
        seekDuration = audioData.length / WAVEFORM_ENVELOPE_RATE;
      }
    }
    
    if (seekDuration <= 0) return;
    
    try {
      audioEl.currentTime = seekDuration;
      setCurrentTime(seekDuration);
    } catch (error) {
      console.error('Go to end failed:', error);
    }
  };

  // Helper function for consistent audio element detection
  const getAudioElement = () => {
    // Use the same robust detection as keyboard handlers
    let currentAudioElement = null;
    
    if (audioElementRef.current && audioElementRef.current.readyState >= 1) {
      currentAudioElement = audioElementRef.current;
    }
    
    if (!currentAudioElement && audioElement) {
      currentAudioElement = audioElement;
    }
    
    if (!currentAudioElement && audioUrl) {
      currentAudioElement = document.querySelector(`audio[src="${audioUrl}"]`);
      if (!currentAudioElement) {
        const allAudio = document.querySelectorAll('audio');
        for (let audioEl of allAudio) {
          if (audioEl.src === audioUrl || audioEl.currentSrc === audioUrl) {
            currentAudioElement = audioEl;
            break;
          }
        }
      }
      
      if (!currentAudioElement) {
        const allAudio = document.querySelectorAll('audio');
        for (let audioEl of allAudio) {
          if (audioEl.readyState >= 1 && (audioEl.duration > 0 || !isNaN(audioEl.duration))) {
            currentAudioElement = audioEl;
            setAudioElement(currentAudioElement);
            audioElementRef.current = currentAudioElement;
            break;
          }
        }
      }
    }
    
    return currentAudioElement;
  };

  // Start playback safely: remember the play() promise and swallow the AbortError
  // that fires when a pause()/seek interrupts a still-pending play().
  const safePlay = (audioEl) => {
    if (!audioEl) return;
    try {
      const p = audioEl.play();
      if (p && typeof p.then === 'function') {
        playPromiseRef.current = p;
        p.catch(() => { /* interrupted by pause()/seek -> ignore */ });
      }
    } catch (e) {
      /* ignore */
    }
  };

  // Pause safely: if a play() is still pending, defer the pause until it resolves
  // so the play promise settles cleanly instead of rejecting.
  const safePause = (audioEl) => {
    if (!audioEl) return;
    const p = playPromiseRef.current;
    if (p && typeof p.then === 'function') {
      p.then(() => { try { audioEl.pause(); } catch (e) { /* ignore */ } })
       .catch(() => { /* already rejected/aborted */ });
    } else {
      try { audioEl.pause(); } catch (e) { /* ignore */ }
    }
  };

  const wfBtnStyle = {
    padding: '2px 6px',
    fontSize: '12px',
    lineHeight: 1.2,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer'
  };
  const formatClock = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

  // Scrollbar thumb geometry (derived from the current view / amplitude state).
  const scrollTotal = getTotalDuration();
  const scrollViewStart = Math.max(0, viewStart);
  const scrollViewEnd = viewEnd > viewStart ? viewEnd : scrollTotal;
  const scrollViewSpan = Math.max(0.001, scrollViewEnd - scrollViewStart);
  const hThumbWidthPct = scrollTotal > 0 ? Math.min(100, (scrollViewSpan / scrollTotal) * 100) : 100;
  const hThumbLeftPct = scrollTotal > 0 ? Math.max(0, Math.min(100 - hThumbWidthPct, (scrollViewStart / scrollTotal) * 100)) : 0;
  const hCanScroll = hThumbWidthPct < 99.9;
  const vMaxY = 0.8 * ampScale;
  const vCanScroll = vMaxY > 1.001;
  const vThumbHeightPct = vCanScroll ? Math.min(100, (1 / vMaxY) * 100) : 100;
  const vThumbTopPct = (1 - ampOffset) * (100 - vThumbHeightPct);

  return (
    <div className="waveform-container" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', marginTop: '4px' }}>

        <h4 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280' }}>Waveform</h4>
        
        {/* Gain Control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label htmlFor="gain-slider" style={{ fontSize: '12px', color: '#666' }}>
            Gain: {(20 * Math.log10(gain)).toFixed(1)} dB
          </label>
          <input
            id="gain-slider"
            type="range"
            min="-40"
            max="60"
            step="0.5"
            value={20 * Math.log10(gain)}
            onChange={(e) => {
              const dB = parseFloat(e.target.value);
              const newGain = Math.pow(10, dB / 20);
              setGain(newGain);
            }}
            style={{
              width: '120px',
              cursor: 'pointer'
            }}
          />
        </div>
      </div>

      {/* Zoom / selection / loop controls — operate on the streamed envelope + <audio> element */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '12px' }}>
        <span style={{ color: '#6b7280' }}>Time:</span>
        <button type="button" onClick={() => zoomTime(1.4)} title="Zoom in (or scroll on the waveform)" style={wfBtnStyle}>+</button>
        <button type="button" onClick={() => zoomTime(1 / 1.4)} title="Zoom out" style={wfBtnStyle}>−</button>
        <button type="button" onClick={zoomFit} title="Fit whole recording" style={wfBtnStyle}>Fit</button>
        <button type="button" onClick={zoomToSelection} disabled={!selection} title="Zoom to selection" style={{ ...wfBtnStyle, opacity: selection ? 1 : 0.5 }}>To sel.</button>
        <span style={{ color: '#6b7280', marginLeft: '8px' }}>Amp:</span>
        <button type="button" onClick={() => changeAmp(1.4)} title="Amplitude zoom in (or Ctrl/⌘ + scroll)" style={wfBtnStyle}>+</button>
        <button type="button" onClick={() => changeAmp(1 / 1.4)} title="Amplitude zoom out" style={wfBtnStyle}>−</button>
        <button type="button" onClick={() => { setAmpScale(1); setAmpOffset(0); }} title="Reset amplitude" style={wfBtnStyle}>Fit</button>
        <span style={{ width: '1px', height: '18px', background: '#e0e0e0', margin: '0 4px' }} />
        <button type="button" onClick={playSelectionRegion} disabled={!selection} title="Play selected region" style={{ ...wfBtnStyle, opacity: selection ? 1 : 0.5 }}>▶ Sel.</button>
        <button type="button" onClick={clearSelection} disabled={!selection} title="Clear selection" style={{ ...wfBtnStyle, opacity: selection ? 1 : 0.5 }}>Clear</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666', cursor: 'pointer' }}>
          <input type="checkbox" checked={loopEnabled} onChange={(e) => setLoopEnabled(e.target.checked)} />
          Loop
        </label>
        {selection && (
          <span style={{ color: '#8a6d1f', marginLeft: 'auto' }}>
            {formatClock(selection.start)}–{formatClock(selection.end)} ({(selection.end - selection.start).toFixed(2)}s)
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'stretch', gap: '2px' }}>
        <div ref={waveformRef} style={{ flex: 1, height: '192px' }} />
        {/* Vertical scrollbar: amplitude pan (active only when amplitude-zoomed) */}
        <div
          onMouseDown={vCanScroll ? beginVScrollDrag : undefined}
          title={vCanScroll ? 'Drag to pan amplitude' : 'Amplitude pan (zoom amplitude to enable)'}
          style={{
            position: 'relative',
            width: '12px',
            height: '192px',
            background: '#f1f3f5',
            borderRadius: '6px',
            cursor: vCanScroll ? 'ns-resize' : 'default',
            opacity: vCanScroll ? 1 : 0.5,
            flex: '0 0 auto'
          }}
        >
          <div style={{
            position: 'absolute',
            left: '2px',
            right: '2px',
            top: `${vThumbTopPct}%`,
            height: `${vThumbHeightPct}%`,
            background: vCanScroll ? '#adb5bd' : '#ced4da',
            borderRadius: '5px',
            minHeight: '16px'
          }} />
        </div>
      </div>
      {/* Horizontal scrollbar: time pan (active only when zoomed in) */}
      <div
        onMouseDown={hCanScroll ? beginHScrollDrag : undefined}
        title={hCanScroll ? 'Drag to scroll through time' : 'Time scroll (zoom in to enable)'}
        style={{
          position: 'relative',
          width: 'calc(100% - 14px)',
          height: '12px',
          marginTop: '3px',
          background: '#f1f3f5',
          borderRadius: '6px',
          cursor: hCanScroll ? 'ew-resize' : 'default',
          opacity: hCanScroll ? 1 : 0.5
        }}
      >
        <div style={{
          position: 'absolute',
          top: '2px',
          bottom: '2px',
          left: `${hThumbLeftPct}%`,
          width: `${hThumbWidthPct}%`,
          background: hCanScroll ? '#adb5bd' : '#ced4da',
          borderRadius: '5px',
          minWidth: '16px'
        }} />
      </div>

      {/* Listening aids: playback rate, preview filter, measure tool, transient finder. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '12px' }}>
        <span style={{ color: '#6b7280' }}>Rate:</span>
        <select value={playbackRate} onChange={(e) => setPlaybackRate(Number(e.target.value))} style={{ ...wfBtnStyle, padding: '2px 4px' }} title="Playback speed">
          {[0.25, 0.5, 0.75, 1, 1.5, 2].map((r) => <option key={r} value={r}>{r}×</option>)}
        </select>
        <span style={{ width: '1px', height: '18px', background: '#e0e0e0', margin: '0 4px' }} />
        <span style={{ color: '#6b7280' }}>Filter:</span>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ ...wfBtnStyle, padding: '2px 4px' }} title="Preview filter (affects playback only)">
          <option value="off">Off</option>
          <option value="highpass">High-pass</option>
          <option value="lowpass">Low-pass</option>
          <option value="bandpass">Band-pass</option>
        </select>
        {filterType !== 'off' && (
          <>
            <input
              type="range" min="20" max="20000" step="1"
              value={filterFreq}
              onChange={(e) => setFilterFreq(Number(e.target.value))}
              style={{ width: '110px' }}
              title="Cutoff / center frequency"
            />
            <span style={{ color: '#6b7280', fontFamily: 'monospace', minWidth: '58px' }}>
              {filterFreq >= 1000 ? `${(filterFreq / 1000).toFixed(1)}k` : filterFreq} Hz
            </span>
          </>
        )}
        <span style={{ width: '1px', height: '18px', background: '#e0e0e0', margin: '0 4px' }} />
        <button
          type="button"
          onClick={() => { setMeasureMode((m) => !m); setMeasurePoints([]); }}
          title="Measure the time between two clicks"
          style={{ ...wfBtnStyle, background: measureMode ? '#fef3c7' : '#fff', borderColor: measureMode ? '#d4af37' : '#d1d5db' }}
        >
          📏 Measure
        </button>
        {measureMode && measurePoints.length === 2 && (
          <span style={{ color: '#8a6d1f', fontFamily: 'monospace' }}>
            Δt {Math.abs(measurePoints[1] - measurePoints[0]).toFixed(3)}s · {(1 / Math.max(1e-6, Math.abs(measurePoints[1] - measurePoints[0]))).toFixed(2)} Hz
          </span>
        )}
        <span style={{ width: '1px', height: '18px', background: '#e0e0e0', margin: '0 4px' }} />
        <button type="button" onClick={detectTransients} title="Auto-mark loud onsets (e.g. knocks)" style={wfBtnStyle}>⚡ Find transients</button>
        {transients.length > 0 && (
          <button type="button" onClick={() => setTransients([])} title="Clear transient markers" style={wfBtnStyle}>Clear ({transients.length})</button>
        )}
      </div>

      {/* Interactive Comment Markers Overlay */}
      {comments && comments.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 'calc(100% - 14px)',
          height: '192px',
          pointerEvents: 'none', // Allow clicks to pass through to waveform
          zIndex: 10
        }}>
          {comments.map((comment, index) => {
            // A comment with no timeFrom is a recording-wide label, not a timeline
            // marker — skip it (it has no meaningful position on the waveform).
            if (!comment || !comment.timeFrom) return null;
            const canvas = lastCanvasRef.current;
            if (!canvas) return null;
            
            const audioData = accumulatedSamplesRef.current;
            if (audioData.length === 0) return null;

            // Map the marker into the current (possibly zoomed) view window and hide
            // markers that fall outside it.
            const view = getView();
            const viewSpan = view.end - view.start;
            const timeFromSeconds = timeStringToSeconds(comment.timeFrom);
            if (timeFromSeconds < view.start || timeFromSeconds > view.end || viewSpan <= 0) return null;

            const xPercentage = ((timeFromSeconds - view.start) / viewSpan) * 100;

            if (xPercentage < 0 || xPercentage > 100) return null;
            
            const isHighlighted = highlightedCommentUuid === comment.uuid;
            
            return (
              <div
                key={comment.uuid}
                onClick={() => onCommentClick && onCommentClick(comment)}
                title={`${comment.author}: ${comment.text}`}
                style={{
                  position: 'absolute',
                  left: `${xPercentage}%`,
                  top: '50px', // Position at y=30 like the canvas markers
                  transform: 'translateX(-50%)', // Center the marker
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: isHighlighted 
                    ? 'rgba(212, 175, 55, 1)' 
                    : 'rgba(212, 175, 55, 0.8)',
                  border: `2px solid ${isHighlighted ? '#d4af37' : 'rgba(212, 175, 55, 1)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  pointerEvents: 'auto', // Enable clicks on markers
                  transition: 'all 0.2s ease',
                  boxShadow: isHighlighted 
                    ? '0 0 8px rgba(212, 175, 55, 0.6)' 
                    : '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: isHighlighted ? 20 : 15
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateX(-50%) scale(1.1)';
                  e.target.style.backgroundColor = 'rgba(212, 175, 55, 1)';
                }}
                onMouseLeave={(e) => {
                  if (!isHighlighted) {
                    e.target.style.transform = 'translateX(-50%) scale(1)';
                    e.target.style.backgroundColor = 'rgba(212, 175, 55, 0.8)';
                  }
                }}
              >
                💬
              </div>
            );
          })}
        </div>
      )}
      
      {/* Playback control buttons */}
      {audioElement && duration > 0 && (
        <div style={{ 
          marginTop: '10px', 
          marginBottom: '8px',
          display: 'flex', 
          gap: '10px', 
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          {
          <button 
            onClick={handleGoToStart}
            style={{
              padding: '6px 14px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.78rem',
              fontWeight: '500',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Go to beginning"
          >
            ⏮️ Start
          </button>
          }
          {/*
          <button 
            onClick={() => handleSeek(-5)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Skip back 5 seconds"
          >
            ⏪ -5s
          </button>
          */}
          <button 
            onClick={handlePlayPause}
            style={{
              padding: '8px 22px',
              backgroundColor: '#d4af37',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              letterSpacing: '0.03em',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              boxShadow: '0 1px 3px rgba(212, 175, 55, 0.35)'
            }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸️ Pause' : '▶️ Play'}
          </button>

          <button
            onClick={handleStop}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ffffff',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
            title="Stop and return to start"
          >
            ⏹️ Stop
          </button>

          {/*
          <button 
            onClick={() => handleSeek(5)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Skip forward 5 seconds"
          >
            ⏩ +5s
          </button>
          */}

          {
          <button 
            onClick={handleGoToEnd}
            style={{
              padding: '6px 14px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.78rem',
              fontWeight: '500',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Go to end"
          >
            ⏭️ End
          </button>
          }
          
          <div>
            <span style={{ color: '#6b7280', fontSize: '0.82rem', fontFamily: 'monospace', letterSpacing: '0.04em' }}>
              {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')} / 
              {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}
            </span>
          </div>

        </div>
      )}
      
      {/*
      <div className="waveform-info" style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
        <div>
          {hasDecodedAnyDataRef.current ? (
            <span>
              🎵 Audio processing 
              {isStreaming ? ' (ongoing)' : ' (complete)'}
              {accumulatedSamplesRef.current.length > 0 && (
                <span style={{ marginLeft: '8px' }}>
                  ({Math.floor(accumulatedSamplesRef.current.length / (audioContextRef.current?.sampleRate || 48000))}s decoded)
                </span>
              )}
            </span>
          ) : (
            <span>
              📊 Awaiting audio data
              {isStreaming ? ' (downloading...)' : ' (complete)'}
            </span>
          )}
        </div>
        
        {audioElement && duration > 0 && (
          <div style={{ marginTop: '4px' }}>
            <span style={{ color: isPlaying ? '#ff4444' : '#999' }}>
              {isPlaying ? '▶️' : '⏸️'} 
              {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')} / 
              {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}
            </span>
            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#aaa' }}>
              🖱️ Click waveform to seek
            </span>
          </div>
        )}
        
      </div>
      */}

      {/* Right-click menu for the selected region. */}
      {contextMenu && (
        <>
          <div
            onClick={() => setContextMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
            style={{ position: 'fixed', inset: 0, zIndex: 10000 }}
          />
          <div
            style={{
              position: 'fixed',
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              zIndex: 10001,
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              padding: '4px',
              minWidth: '190px'
            }}
          >
            {[
              { label: '📈 Spectrum', fn: () => analyzeSelection('spectrum') },
              { label: '🌈 Spectrogram', fn: () => analyzeSelection('spectrogram') },
              { label: '📊 Statistics', fn: () => analyzeSelection('stats') },
              { label: '💾 Export selection as WAV', fn: exportSelectionWav }
            ].map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.fn}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px',
                  border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', color: '#374151'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Tabbed analysis modal (spectrum / spectrogram / statistics). */}
      {(analysis || analysisBusy) && (
        <div
          onClick={() => { setAnalysis(null); setAnalysisBusy(false); setSpecCursor(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0b1020', color: '#e5e7eb', borderRadius: '10px', padding: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.4)', maxWidth: '92vw'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['spectrum', 'spectrogram', 'stats'].map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setAnalysisTab(tab)}
                    style={{
                      padding: '4px 10px', fontSize: '12px', borderRadius: '5px', cursor: 'pointer',
                      border: '1px solid ' + (analysisTab === tab ? '#4ade80' : '#334155'),
                      background: analysisTab === tab ? '#14532d' : 'transparent',
                      color: analysisTab === tab ? '#d1fae5' : '#9ca3af', textTransform: 'capitalize'
                    }}
                  >
                    {tab === 'stats' ? 'Statistics' : tab}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setAnalysis(null); setAnalysisBusy(false); setSpecCursor(null); }}
                style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}
                title="Close"
              >
                ×
              </button>
            </div>

            {analysis && !analysis.error && (
              <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px' }}>
                {formatClock(analysis.startSec)}–{formatClock(analysis.endSec)} ({(analysis.endSec - analysis.startSec).toFixed(2)}s) · {(analysis.sampleRate / 1000).toFixed(1)} kHz
                {analysis.note && (
                  <span style={{ color: '#fbbf24', marginLeft: '8px' }}>
                    ♪ {analysis.note.name} ({Math.round(analysis.note.freq)} Hz{analysis.note.cents ? `, ${analysis.note.cents > 0 ? '+' : ''}${analysis.note.cents}¢` : ''})
                  </span>
                )}
              </div>
            )}

            {analysisBusy && (
              <div style={{ width: '640px', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '13px' }}>
                Analyzing…
              </div>
            )}
            {analysis && analysis.error && (
              <div style={{ width: '640px', padding: '40px 20px', textAlign: 'center', color: '#f87171', fontSize: '13px' }}>
                {analysis.error}
              </div>
            )}

            {analysis && !analysis.error && analysisTab === 'spectrum' && (
              <div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontSize: '12px', color: '#9ca3af', alignItems: 'center' }}>
                  <label style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>
                    Scale:
                    <select value={specScale} onChange={(e) => setSpecScale(e.target.value)} style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #334155', borderRadius: '4px' }}>
                      <option value="log">Log</option>
                      <option value="linear">Linear</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>
                    Mode:
                    <select value={specMode} onChange={(e) => setSpecMode(e.target.value)} style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #334155', borderRadius: '4px' }}>
                      <option value="avg">Average</option>
                      <option value="peak">Peak hold</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', gap: '4px', cursor: 'pointer', alignItems: 'center' }}>
                    <input type="checkbox" checked={specHarmonics} onChange={(e) => setSpecHarmonics(e.target.checked)} />
                    Harmonics
                  </label>
                </div>
                <canvas
                  ref={spectrumCanvasRef}
                  width={640}
                  height={300}
                  onMouseMove={handleSpectrumHover}
                  onMouseLeave={() => setSpecCursor(null)}
                  style={{ display: 'block', borderRadius: '6px', cursor: 'crosshair' }}
                />
                <div style={{ marginTop: '8px', color: '#6b7280', fontSize: '11px' }}>
                  Frequency (Hz, {specScale}) vs. magnitude (dB, peak = 0). Hover to read a bin.
                </div>
              </div>
            )}

            {analysis && !analysis.error && analysisTab === 'spectrogram' && (
              <div>
                <canvas ref={spectrogramCanvasRef} width={640} height={300} style={{ display: 'block', borderRadius: '6px' }} />
                <div style={{ marginTop: '8px', color: '#6b7280', fontSize: '11px' }}>
                  Time → · frequency ↑ (0–{(analysis.sampleRate / 2000).toFixed(1)} kHz) · brighter = louder (80 dB range).
                </div>
              </div>
            )}

            {analysis && !analysis.error && analysisTab === 'stats' && analysis.stats && (
              <div style={{ width: '640px', fontSize: '13px' }}>
                {[
                  ['Duration', `${analysis.stats.durationSec.toFixed(3)} s (${analysis.stats.samples} samples)`],
                  ['Peak level', `${analysis.stats.peakDb.toFixed(1)} dBFS`],
                  ['RMS level', `${analysis.stats.rmsDb.toFixed(1)} dBFS`],
                  ['Crest factor', `${analysis.stats.crestDb.toFixed(1)} dB`],
                  ['DC offset', analysis.stats.dcOffset.toFixed(5)],
                  ['Clipped samples', `${analysis.stats.clipped}${analysis.stats.clipped > 0 ? ' ⚠️' : ''}`],
                  ['Zero-cross rate', `${Math.round(analysis.stats.zcrHz)} Hz`],
                  ['Detected pitch', analysis.note ? `${analysis.note.name} · ${Math.round(analysis.note.freq)} Hz` : '— (broadband / non-tonal)']
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 4px', borderBottom: '1px solid #1f2937' }}>
                    <span style={{ color: '#9ca3af' }}>{k}</span>
                    <span style={{ fontFamily: 'monospace' }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    
  );
};

export default WaveformViewer;
